import React, {Component, createRef} from "react";
import {connect} from "react-redux";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SearchIcon from "@mui/icons-material/Search";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {AppTitle} from "../components/components";
import {StyledInputBase} from "../components/StyledComponents";
import {aclPermissions, hasPermissions} from "const/ACLPermissions";
import {animateToState} from "../utils/map_utils";
import {
	cancelCursorConnect,
	captureAiViewportScreenshot,
	disconnectCursor,
	listAiGeoFeatures,
	listAiViewRequests,
	synthesizeViewRequestsFromContent,
	groupAiGeoFeaturesByPriority,
	linkifyFeatureRefs,
	placeAiFeaturesOnSlide,
	looksLikeAnnotateIntent,
	looksLikeViewportIntent,
	readVisibleCanvasBox,
	readLiveMapState,
	countUnparsedViewRequestBlocks,
	linkSlideAiChatToCase,
	writePendingHandoff,
	readPendingHandoff,
	clearPendingHandoff,
	waitForMapIdle,
	fulfillViewRequests,
	navigateToMapStateVerified,
	resolveViewRequestMapState,
	mapStateForAiFeature,
	fetchCursorConnectStatus,
	fetchCursorModels,
	fetchCursorStatus,
	archiveSlideAiChat,
	createSlideAiChat,
	fetchSlideAiChat,
	fetchSlideAiJob,
	fetchSlideAiModels,
	fetchSlideAiSystemPrompt,
	switchSlideAiChat,
	renameSlideAiChat,
	isAiUnavailableMessage,
	linkifySiteRefs,
	linkifyRegionRefs,
	linkifyCoordRefs,
	syntheticGeoItem,
	attachmentRegions,
	zoomChangeText,
	magLabelForZ,
	MAX_AI_SITES,
	MAX_VIEW_REQUESTS_PER_ROUND,
	clampAutoViewDepthMax,
	resetSlideAiSystemPrompt,
	saveCursorPrefs,
	saveSlideAiPrefs,
	saveSlideAiSystemPrompt,
	readLocalSlideAiPrefs,
	writeLocalSlideAiPrefs,
	sendChatMessageWithOptionalScreenshot,
	siteLabelAt,
	startCursorConnect,
	stripAiMachineBlocks,
	gammaUrlForMapState,
} from "../utils/slide_ai_utils";
import {subscribeMorphoChatJob} from "../utils/morpho_chat_ws";
import {openAiChatPopout} from "../utils/slide_ai_popout";
import {openTopApp} from "../../../redux_actions/gamma.state.action";
import {addAnnotation} from "../../../redux_actions/maps.state.action";
import {error_manager} from "../../../helper/error_manager";
import {getLoggedInUserId, getStateAwareUrl, isSuperuser} from "../../../utils/utils";
import {ScreenshotExtensions} from "../../../utils/const";
import {AxiosConfig} from "../../../helper/axios.config";
import axios from "axios";
import {notifyInfo} from "../utils/display.notification";

const CHAT_TEXT_COLOR = "#ffffff";
const CURSOR_USAGE_URL = "https://cursor.com/dashboard/usage";
const CURSOR_PROVIDERS = ["cursor", "cursor_cli"];
/** Cursor IDE Auto → Optimize for (docs: cursor.com/docs/cursor-router). */
const AUTO_OPTIMIZE = [
	{id: "cost", label: "Cost", hint: "Optimize token spend (legacy Auto)"},
	{id: "balanced", label: "Balance", hint: "Intelligence, speed, and cost"},
	{id: "intelligence", label: "Intelligence", hint: "Most capable models for harder tasks"},
];

/** Cursor CLI accepts plain "auto" only; strip IDE Optimize For brackets. */
const cliAutoSlug = (slug, family = "auto") => {
	const s = (slug || "").trim();
	if (!s || family === "auto" || s === "auto" || s.startsWith("auto[") || s.startsWith("auto-smart")) {
		return "auto";
	}
	return s;
};

/** Rough context windows for est. % (CLI does not return window size). */
const EST_CONTEXT_WINDOW = {
	auto: 200000,
	"claude-opus": 200000,
	"claude-sonnet": 200000,
	"claude-4": 200000,
	"gpt-5": 200000,
	composer: 200000,
	default: 200000,
};

const formatTokenCount = (n) => {
	const v = Number(n);
	if (!Number.isFinite(v) || v < 0) return null;
	if (v < 1000) return String(Math.round(v));
	if (v < 10000) return `${(v / 1000).toFixed(1)}k`;
	return `${Math.round(v / 1000)}k`;
};

const estimateContextWindow = (model) => {
	const m = String(model || "").toLowerCase();
	if (m.includes("auto")) return EST_CONTEXT_WINDOW.auto;
	if (m.includes("opus")) return EST_CONTEXT_WINDOW["claude-opus"];
	if (m.includes("sonnet")) return EST_CONTEXT_WINDOW["claude-sonnet"];
	if (m.includes("claude")) return EST_CONTEXT_WINDOW["claude-4"];
	if (m.includes("gpt-5") || m.includes("gpt5")) return EST_CONTEXT_WINDOW["gpt-5"];
	if (m.includes("composer")) return EST_CONTEXT_WINDOW.composer;
	return EST_CONTEXT_WINDOW.default;
};

const markdownSx = {
	color: CHAT_TEXT_COLOR,
	fontSize: "0.875rem",
	lineHeight: 1.45,
	wordBreak: "break-word",
	"& > *:first-of-type": {mt: 0},
	"& > *:last-child": {mb: 0},
	"& p": {m: "0.35em 0", color: CHAT_TEXT_COLOR},
	"& h1, & h2, & h3, & h4, & h5, & h6": {
		color: CHAT_TEXT_COLOR,
		fontWeight: 600,
		lineHeight: 1.25,
		mt: "0.75em",
		mb: "0.35em",
	},
	"& h1": {fontSize: "1.15rem"},
	"& h2": {fontSize: "1.05rem"},
	"& h3": {fontSize: "0.98rem"},
	"& ul, & ol": {
		m: "0.35em 0",
		pl: "1.35em",
		color: CHAT_TEXT_COLOR,
	},
	"& li": {mb: "0.2em", color: CHAT_TEXT_COLOR},
	"& li > p": {m: 0},
	"& strong": {fontWeight: 700, color: CHAT_TEXT_COLOR},
	"& em": {fontStyle: "italic", color: CHAT_TEXT_COLOR},
	"& a": {color: "#9ecbff", textDecoration: "underline", cursor: "pointer"},
	"& blockquote": {
		m: "0.5em 0",
		pl: 1.25,
		borderLeft: "3px solid rgba(255,255,255,0.35)",
		color: CHAT_TEXT_COLOR,
		opacity: 0.95,
	},
	"& hr": {
		border: 0,
		borderTop: "1px solid rgba(255,255,255,0.2)",
		my: 1,
	},
	"& code": {
		fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
		fontSize: "0.82em",
		color: CHAT_TEXT_COLOR,
		backgroundColor: "rgba(255,255,255,0.1)",
		px: 0.5,
		py: 0.15,
		borderRadius: 0.5,
	},
	"& pre": {
		m: "0.5em 0",
		p: 1,
		overflow: "auto",
		borderRadius: 1,
		backgroundColor: "rgba(0,0,0,0.35)",
		border: "1px solid rgba(255,255,255,0.12)",
	},
	"& pre code": {
		backgroundColor: "transparent",
		p: 0,
		fontSize: "0.8em",
		whiteSpace: "pre-wrap",
	},
	"& table": {
		width: "100%",
		borderCollapse: "collapse",
		my: 0.75,
		fontSize: "0.8rem",
	},
	"& th, & td": {
		border: "1px solid rgba(255,255,255,0.2)",
		px: 0.75,
		py: 0.35,
		color: CHAT_TEXT_COLOR,
		textAlign: "left",
	},
	"& th": {backgroundColor: "rgba(255,255,255,0.08)", fontWeight: 600},
};

const THINKING_MESSAGES = [
	"Reviewing the slide image…",
	"Examining tissue morphology…",
	"Evaluating tissue architecture…",
	"Assessing cellular features…",
	"Reviewing staining patterns…",
	"Identifying regions of interest…",
	"Correlating morphological findings…",
	"Preparing the image assessment…",
];

const THINKING_ROTATE_MS = 2200;

class AiChatApp extends Component {
	constructor(props) {
		super(props);
		this.listRef = createRef();
		this.blobUrls = {};
		this.thinkingTimer = null;
		this.stickToBottom = true;
		const localPrefs = readLocalSlideAiPrefs();
		this.state = {
			messages: [],
			models: [],
			provider: "gemini",
			modelId: "",
			value: "",
			loading: false,
			loadingChat: false,
			blobSrcByKey: {},
			thinkingMessageIndex: 0,
			capturedImages: [],
			capturing: false,
			lightbox: null, // {src, label}
			savingGeoForMsg: null,
			placingGeoProgress: null, // {msgId, site, phase}
			drawnAiFeatureKeys: {}, // { [msgId]: string[] }
			legendExpandedByMsg: {}, // { [msgId]: boolean }
			sendQueue: [],
			activeQueueId: null,
			cursor: {state: "disconnected", email: null, team: null},
			cursorFamilies: [],
			cursorFamily: "auto",
			cursorOptimizeFor: "balanced",
			cursorModels: [],
			cursorConnecting: false,
			cursorLoginUrl: null,
			cursorQr: null,
			cursorLoginId: null,
			modelMenuAnchor: null,
			modelSubmenu: null, // 'optimize' | 'model' | 'provider' | null
			submenuAnchor: null,
			modelSearch: "",
			annoRequired: false,
			// Off: the model gets only the attached captures, not every full-resolution tile.
			fullAnalysis: true,
			// 0 = off; 1..MAX = auto-capture follow-up rounds (no separate checkbox).
			autoViewDepthMax: clampAutoViewDepthMax(localPrefs.auto_view_depth_max),
			autoViewDepth: 0,
			autoCapturing: false,
			autoCaptureProgress: null, // {done, total, phase, intent}
			chatId: null,
			chatTitle: "Morpho Chat",
			chatSessions: [],
			chatScope: "slide",
			chatCase: null, // {case_id, case_key, parts: [{part, slide_id}], current_part}
			homeSlideId: null,
			linkingCase: false,
			handoffCards: {}, // { [msgId]: {targetSlideId, part, mapState, reason} }
			viewingArchived: false,
			chatMenuAnchor: null,
			renameChatId: null,
			renameInMenu: false,
			renameDraft: "",
			systemPromptOpen: false,
			systemPromptDraft: "",
			systemPromptSource: null,
			systemPromptHasOverride: false,
			systemPromptBusy: false,
			systemPromptLoading: false,
		};
		this.lastSend = null;
		this.connectPollTimer = null;
		this.jobPollTimers = {};
		this.jobWsClosers = {};
		this.queueBusy = false;
		this.inFlightQueueIds = new Set();
	}

	componentWillUnmount() {
		this.stopThinkingRotation();
		this.stopConnectPoll();
		Object.values(this.jobPollTimers).forEach((t) => clearInterval(t));
		this.jobPollTimers = {};
		Object.values(this.jobWsClosers || {}).forEach((c) => {
			try { c.close(); } catch (_) { /* ignore */ }
		});
		this.jobWsClosers = {};
		Object.values(this.blobUrls).forEach((u) => {
			if (typeof u === "string" && u.startsWith("blob:")) {
				try { URL.revokeObjectURL(u); } catch (e) { /* ignore */ }
			}
		});
		this.blobUrls = {};
		(this.state.capturedImages || []).forEach((s) => {
			if (s.previewUrl) {
				try { URL.revokeObjectURL(s.previewUrl); } catch (e) { /* ignore */ }
			}
		});
	}

	stopConnectPoll = () => {
		if (this.connectPollTimer) {
			clearInterval(this.connectPollTimer);
			this.connectPollTimer = null;
		}
	};

	isCursorProvider = () => CURSOR_PROVIDERS.includes(this.state.provider);

	openSystemPromptEditor = () => {
		if (!isSuperuser()) return;
		this.closeModelMenu();
		this.setState({
			systemPromptOpen: true,
			systemPromptLoading: true,
			systemPromptDraft: "",
			systemPromptSource: null,
			systemPromptHasOverride: false,
		});
		fetchSlideAiSystemPrompt()
			.then((data) => {
				this.setState({
					systemPromptDraft: data.prompt || "",
					systemPromptSource: data.source || null,
					systemPromptHasOverride: !!data.has_override,
					systemPromptLoading: false,
				});
			})
			.catch((err) => {
				this.setState({systemPromptOpen: false, systemPromptLoading: false});
				error_manager("Failed to load system prompt", err);
			});
	};

	closeSystemPromptEditor = () => {
		if (this.state.systemPromptBusy) return;
		this.setState({systemPromptOpen: false});
	};

	saveSystemPromptEditor = () => {
		if (!isSuperuser() || this.state.systemPromptBusy) return;
		this.setState({systemPromptBusy: true});
		saveSlideAiSystemPrompt(this.state.systemPromptDraft)
			.then((data) => {
				this.setState({
					systemPromptBusy: false,
					systemPromptDraft: data.prompt || this.state.systemPromptDraft,
					systemPromptSource: data.source || "override",
					systemPromptHasOverride: !!data.has_override,
					systemPromptOpen: false,
				});
			})
			.catch((err) => {
				this.setState({systemPromptBusy: false});
				error_manager("Failed to save system prompt", err);
			});
	};

	resetSystemPromptEditor = () => {
		if (!isSuperuser() || this.state.systemPromptBusy) return;
		if (!window.confirm("Reset to the bundled Morpho Chat system prompt?")) return;
		this.setState({systemPromptBusy: true});
		resetSlideAiSystemPrompt()
			.then((data) => {
				this.setState({
					systemPromptBusy: false,
					systemPromptDraft: data.prompt || data.bundled_prompt || "",
					systemPromptSource: data.source || "bundled",
					systemPromptHasOverride: !!data.has_override,
				});
			})
			.catch((err) => {
				this.setState({systemPromptBusy: false});
				error_manager("Failed to reset system prompt", err);
			});
	};

	startThinkingRotation = () => {
		this.stopThinkingRotation();
		this.setState({thinkingMessageIndex: 0});
		this.thinkingTimer = setInterval(() => {
			this.setState((prev) => ({
				thinkingMessageIndex: (prev.thinkingMessageIndex + 1) % THINKING_MESSAGES.length,
			}));
		}, THINKING_ROTATE_MS);
	};

	stopThinkingRotation = () => {
		if (this.thinkingTimer) {
			clearInterval(this.thinkingTimer);
			this.thinkingTimer = null;
		}
	};

	componentDidMount() {
		if (this.props.variant !== "popout" && this.props.variant !== "dashboard") {
			// Morpho Chat only lives on the right; redirect any left-sidebar mount.
			this.props.dispatch(openTopApp(false));
			openAiChatPopout();
			return;
		}
		this.loadModels();
		this.loadChat(this.props.initialChatId || this.consumeUrlChatId());
	}

	/** ?chat_id= from a cross-part link opens that (case-linked) chat once. */
	consumeUrlChatId = () => {
		if (this.urlChatIdUsed || this.props.variant === "dashboard") return null;
		this.urlChatIdUsed = true;
		try {
			return new URLSearchParams(window.location.search || "").get("chat_id") || null;
		} catch (e) {
			return null;
		}
	};

	scopeStateFrom = (data) => ({
		chatScope: data?.scope === "case" ? "case" : "slide",
		chatCase: data && "case" in data ? (data.case || null) : this.state.chatCase,
		homeSlideId: data?.home_slide_id || null,
	});

	isCaseChat = () => this.state.chatScope === "case" && Boolean(this.state.chatCase);

	partForSlide = (slideId) => {
		const parts = this.state.chatCase?.parts || [];
		return parts.find((p) => String(p.slide_id) === String(slideId))?.part || null;
	};

	/** Slide that captured an attachment (legacy attachments live on the chat's home slide). */
	attSlideId = (att) => att?.slide_id || this.state.homeSlideId || this.props.slide?.id || null;

	isOtherPart = (att) => {
		const sid = this.attSlideId(att);
		return Boolean(sid && this.props.slide?.id && String(sid) !== String(this.props.slide.id));
	};

	toggleCaseLink = (link) => {
		const chatId = this.state.chatId;
		if (!this.props.slide?.id || !chatId || this.state.linkingCase) return;
		this.setState({linkingCase: true});
		linkSlideAiChatToCase(this.props.slide.id, chatId, link, {includeArchived: this.includeArchivedSessions()})
			.then((data) => {
				const messages = this.assignSerialSiteLabels(data.messages || []);
				this.setState({
					...this.scopeStateFrom(data),
					linkingCase: false,
					chatSessions: data.sessions?.chats || this.state.chatSessions,
					...(data.chat_id !== chatId ? {messages, chatId: data.chat_id, chatTitle: data.title || "Morpho Chat"} : {}),
				});
				notifyInfo("Morpho Chat", link
					? `Chat linked to case ${data.case?.case_key || ""} — open it from any part`
					: "Chat unlinked from the case");
			})
			.catch((err) => {
				this.setState({linkingCase: false});
				error_manager(link ? "Could not link chat to case" : "Could not unlink chat", err);
			});
	};

	componentDidUpdate(prevProps, prevState) {
		if (
			prevProps.slide?.id !== this.props.slide?.id
			|| prevProps.initialChatId !== this.props.initialChatId
		) {
			this.loadChat(this.props.initialChatId || null);
			this.clearCapturedImages();
		}
		// Renamed from the dashboard list
		if (this.props.chatTitle && prevProps.chatTitle !== this.props.chatTitle) {
			this.setState({chatTitle: this.props.chatTitle});
		}
		if (prevState.messages !== this.state.messages || prevProps.slide?.id !== this.props.slide?.id) {
			this.prefetchAttachmentBlobs(this.state.messages);
		}
		if (prevState.provider !== this.state.provider && this.isCursorProvider()) {
			this.refreshCursorStatus();
			this.refreshCursorModels();
		}
	}

	clearCapturedImages = () => {
		(this.state.capturedImages || []).forEach((s) => {
			if (s.previewUrl) {
				try { URL.revokeObjectURL(s.previewUrl); } catch (e) { /* ignore */ }
			}
		});
		this.setState({capturedImages: []});
	};

	prefetchAttachmentBlobs = (messages) => {
		(messages || []).forEach((msg) => {
			(msg.attachments || []).forEach((att) => {
				if (this.attachmentSrc(att)) return;
				if (att?.rel_path) this.ensureBlobSrc(att);
			});
		});
	};

	loadModels = () => {
		if (!hasPermissions([aclPermissions.ai_chat_app])) return;
		const localPrefs = readLocalSlideAiPrefs();
		fetchSlideAiModels()
			.then((data) => {
				const models = data.models || [];
				const defaultP = data.default_provider || "gemini";
				const lastP = data.last_provider || localPrefs.last_provider || null;
				const lastM = data.last_model_id || localPrefs.last_model_id || null;
				const byLast = lastP
					? (models.find((m) => m.provider === lastP && m.configured)
						|| models.find((m) => m.provider === lastP))
					: null;
				const preferred =
					byLast ||
					models.find((m) => m.provider === defaultP && m.configured) ||
					models.find((m) => m.provider === defaultP) ||
					models.find((m) => m.configured) ||
					models[0];
				const provider = preferred?.provider || lastP || defaultP;
				let modelId = preferred?.model_id || "";
				if (lastM && (!lastP || lastP === provider)) {
					modelId = lastM;
				}
				writeLocalSlideAiPrefs({provider, modelId});
				this.setState({
					models,
					provider,
					modelId,
				}, () => {
					if (this.isCursorProvider()) {
						this.refreshCursorStatus();
						this.refreshCursorModels();
					}
				});
			})
			.catch(() => {
				if (localPrefs.last_provider) {
					this.setState({
						provider: localPrefs.last_provider,
						modelId: localPrefs.last_model_id || "",
					});
				}
			});
	};

	persistProviderModel = (provider = this.state.provider, modelId = this.state.modelId) => {
		writeLocalSlideAiPrefs({provider, modelId});
		saveSlideAiPrefs({provider, modelId}).catch(() => {});
	};

	refreshCursorStatus = () => {
		fetchCursorStatus(true)
			.then((data) => {
				if (!data?.ok && data?.error_code === "runner_unavailable") {
					this.setState({cursor: {state: "runner_down", email: null, team: null}});
					return;
				}
				this.setState({
					cursor: {
						state: data.state || "disconnected",
						email: data.email || null,
						team: data.team || null,
					},
				});
				if (data.prefs) {
					const of = data.prefs.optimize_for || "balanced";
					const family = data.prefs.model_family || "auto";
					this.setState({
						cursorFamily: family,
						cursorOptimizeFor: of === "balance" ? "balanced" : of,
						modelId: cliAutoSlug(data.prefs.model_slug || this.state.modelId || "auto", family),
					});
				}
			})
			.catch(() => {});
	};

	refreshCursorModels = (refresh = false) => {
		fetchCursorModels(refresh)
			.then((data) => {
				if (!data?.ok) return;
				const prefs = data.prefs || {};
				const family = prefs.model_family || this.state.cursorFamily || "auto";
				const optimizeFor = (prefs.optimize_for === "balance" ? "balanced" : prefs.optimize_for)
					|| this.state.cursorOptimizeFor || "balanced";
				const models = (data.models || []).filter((m) => m.slug && m.slug !== "auto");
				const slug = cliAutoSlug(
					prefs.model_slug || (family === "auto" ? "auto" : this.state.modelId),
					family,
				);
				this.setState({
					cursorFamilies: data.families || [],
					cursorModels: models,
					cursorFamily: family,
					cursorOptimizeFor: optimizeFor,
					modelId: slug,
					cursor: {
						state: "connected",
						email: this.state.cursor.email,
						team: this.state.cursor.team,
					},
				}, () => {
					this.persistProviderModel(this.state.provider, slug);
				});
			})
			.catch(() => {});
	};

	cleanModelLabel = (label, slug) => {
		const raw = (label || slug || "").replace(/\u200b/g, "").trim();
		return raw.replace(/\s*\(NO ZDR\)\s*/i, "").trim() || slug;
	};

	autoModeLabel = (optimizeFor = this.state.cursorOptimizeFor) => {
		const of = AUTO_OPTIMIZE.find((o) => o.id === optimizeFor);
		return `Auto ${of?.label || "Balance"}`;
	};

	cursorSelectionLabel = () => {
		if (!this.isCursorProvider()) {
			const m = (this.state.models || []).find((x) => x.provider === this.state.provider);
			return (m?.label || this.state.provider || "Model").replace(/\s*\(.*\)$/, "");
		}
		if ((this.state.cursorFamily || "auto") === "auto" || String(this.state.modelId || "").startsWith("auto")) {
			return this.autoModeLabel();
		}
		const hit = (this.state.cursorModels || []).find((m) => m.slug === this.state.modelId);
		return this.cleanModelLabel(hit?.label, this.state.modelId);
	};

	persistCursorSelection = ({family, optimizeFor, modelSlug} = {}) => {
		const nextFamily = family != null ? family : this.state.cursorFamily;
		const nextOpt = optimizeFor != null ? optimizeFor : this.state.cursorOptimizeFor;
		let slug = modelSlug;
		if (!slug) {
			// Cursor CLI only accepts catalog slugs (plain "auto"). Optimize For is UI prefs only.
			slug = nextFamily === "auto" ? "auto" : this.state.modelId;
		}
		if (typeof slug === "string" && slug.startsWith("auto[")) {
			slug = "auto";
		}
		this.setState({
			cursorFamily: nextFamily,
			cursorOptimizeFor: nextOpt,
			modelId: slug,
		});
		saveCursorPrefs({
			model_family: nextFamily,
			optimize_for: nextOpt,
			model_slug: slug,
		}).catch(() => {});
		this.persistProviderModel(this.state.provider, slug);
	};

	/** Re-label every attachment A, B, C… in chat order (no duplicates). */
	assignSerialSiteLabels = (messages) => {
		let i = 0;
		return (messages || []).map((m) => {
			const atts = m.attachments || [];
			if (!atts.length) return m;
			return {
				...m,
				attachments: atts.map((a) => ({...a, label: siteLabelAt(i++)})),
			};
		});
	};

	/** Next Image A/B/C… unused in this chat (messages + capture tray). */
	nextChatSiteLabel = () => {
		const used = new Set();
		(this.state.messages || []).forEach((m) => {
			(m.attachments || []).forEach((a) => {
				if (a?.label) used.add(String(a.label).toUpperCase());
			});
		});
		(this.state.capturedImages || []).forEach((s) => {
			if (s?.label) used.add(String(s.label).toUpperCase());
		});
		for (let i = 0; i < 200; i += 1) {
			const label = siteLabelAt(i);
			if (!used.has(label)) return label;
		}
		return siteLabelAt(Date.now() % 1000);
	};

	handleCursorConnect = () => {
		this.setState({cursorConnecting: true, cursorLoginUrl: null, cursorQr: null});
		startCursorConnect()
			.then((data) => {
				if (!data?.ok) {
					this.setState({cursorConnecting: false});
					error_manager("Cursor sign-in failed", data?.message || data?.error_code);
					return;
				}
				this.setState({
					cursorLoginId: data.login_id,
					cursorLoginUrl: data.login_url,
					cursorQr: data.qr_data_url || null,
					cursor: {state: "login_pending", email: null, team: null},
				});
				this.stopConnectPoll();
				this.connectPollTimer = setInterval(() => {
					fetchCursorConnectStatus(data.login_id).then((st) => {
						if (st.state === "connected") {
							this.stopConnectPoll();
							this.setState({
								cursorConnecting: false,
								cursorLoginUrl: null,
								cursorQr: null,
								cursor: {state: "connected", email: st.email, team: st.team},
							});
							this.refreshCursorModels(true);
						} else if (st.state === "login_failed" || st.state === "login_expired") {
							this.stopConnectPoll();
							this.setState({
								cursorConnecting: false,
								cursor: {state: st.state, email: null, team: null},
							});
						}
					}).catch(() => {});
				}, 2000);
			})
			.catch((err) => {
				this.setState({cursorConnecting: false});
				error_manager("Cursor sign-in failed", err);
			});
	};

	handleCursorCancelConnect = () => {
		this.stopConnectPoll();
		cancelCursorConnect().finally(() => {
			this.setState({
				cursorConnecting: false,
				cursorLoginUrl: null,
				cursorQr: null,
				cursorLoginId: null,
				cursor: {state: "disconnected", email: null, team: null},
			});
		});
	};

	handleCursorDisconnect = () => {
		if (!window.confirm("Sign out of Cursor on this machine for Morpho Chat?")) return;
		disconnectCursor().then(() => {
			this.setState({
				cursor: {state: "disconnected", email: null, team: null},
				cursorFamilies: [],
				cursorModels: [],
				cursorFamily: "auto",
				modelId: "auto",
			});
		}).catch((err) => error_manager("Cursor sign out failed", err));
	};

	includeArchivedSessions = () => Boolean(isSuperuser());

	loadChat = (chatId = null) => {
		if (!this.props.slide?.id || !hasPermissions([aclPermissions.ai_chat_app])) return;
		this.setState({loadingChat: true});
		const arch = this.includeArchivedSessions();
		fetchSlideAiChat(this.props.slide.id, chatId || this.state.chatId, {includeArchived: arch})
			.then((data) => {
				const messages = this.assignSerialSiteLabels(data.messages || []);
				const sessions = data.sessions?.chats || data.sessions || [];
				this.setState({
					messages,
					chatId: data.chat_id || data.sessions?.active_chat_id || null,
					chatTitle: data.title || "Morpho Chat",
					chatSessions: Array.isArray(sessions) ? sessions : (sessions.chats || []),
					viewingArchived: Boolean(data.archived),
					loadingChat: false,
					...this.scopeStateFrom(data),
				}, () => {
					this.stickToBottom = true;
					this.prefetchAttachmentBlobs(this.state.messages);
					this.scrollToBottom({force: true});
					const live = (messages || []).filter((m) => m.status === "pending" && m.job_id);
					if (live.length) {
						// Hold the send queue exactly like the sender path does; applyJobTerminal's
						// finish() releases it. Without this, an auto follow-up enqueued on job
						// completion is drained twice (once by enqueue, once by finish) → duplicate ask.
						this.queueBusy = true;
						this.setState({loading: true});
						live.forEach((m) => this.pollJobUntilDone(m.job_id, m.id));
					}
					this.maybeConsumeHandoff();
				});
			})
			.catch((err) => {
				this.setState({loadingChat: false});
				error_manager("Failed to load Morpho Chat", err);
			});
	};

	handleNewChat = () => {
		if (!this.props.slide?.id) return;
		this.setState({chatMenuAnchor: null, loadingChat: true});
		const arch = this.includeArchivedSessions();
		createSlideAiChat(this.props.slide.id, null, {includeArchived: arch})
			.then((data) => {
				this.setState({
					messages: [],
					chatId: data.chat_id,
					chatTitle: data.title || "New chat",
					chatSessions: data.sessions?.chats || [],
					viewingArchived: false,
					capturedImages: [],
					loadingChat: false,
					chatScope: "slide",
					homeSlideId: null,
				});
			})
			.catch((err) => {
				this.setState({loadingChat: false});
				error_manager("Could not start new chat", err);
			});
	};

	handleSwitchChat = (chatId) => {
		if (!this.props.slide?.id || !chatId) return;
		this.setState({chatMenuAnchor: null, loadingChat: true});
		const arch = this.includeArchivedSessions();
		switchSlideAiChat(this.props.slide.id, chatId, {includeArchived: arch})
			.then((data) => {
				const messages = this.assignSerialSiteLabels(data.messages || []);
				this.setState({
					messages,
					chatId: data.chat_id,
					chatTitle: data.title || "Morpho Chat",
					chatSessions: data.sessions?.chats || [],
					viewingArchived: Boolean(data.archived),
					capturedImages: [],
					loadingChat: false,
					...this.scopeStateFrom(data),
				}, () => {
					this.stickToBottom = true;
					this.prefetchAttachmentBlobs(this.state.messages);
					this.scrollToBottom({force: true});
				});
			})
			.catch((err) => {
				this.setState({loadingChat: false});
				error_manager("Could not switch chat", err);
			});
	};

	startChatRename = (chatId, title, inMenu = false) => {
		if (!chatId) return;
		this.setState({renameChatId: chatId, renameInMenu: inMenu, renameDraft: title || ""});
	};

	cancelChatRename = () => {
		this.setState({renameChatId: null, renameInMenu: false, renameDraft: ""});
	};

	commitChatRename = () => {
		const {renameChatId: chatId, renameDraft} = this.state;
		if (!chatId) return;
		const title = renameDraft.trim();
		const session = (this.state.chatSessions || []).find((x) => x.chat_id === chatId);
		const previous = session?.title || (chatId === this.state.chatId ? this.state.chatTitle : "");
		this.cancelChatRename();
		if (!title || title === previous || !this.props.slide?.id) return;
		const applyTitle = (newTitle) => this.setState((prev) => ({
			chatSessions: (prev.chatSessions || []).map((x) => (x.chat_id === chatId ? {...x, title: newTitle} : x)),
			chatTitle: prev.chatId === chatId ? newTitle : prev.chatTitle,
		}));
		applyTitle(title);
		renameSlideAiChat(this.props.slide.id, chatId, title, {includeArchived: this.includeArchivedSessions()})
			.then((data) => {
				if (data?.sessions?.chats) this.setState({chatSessions: data.sessions.chats});
				if (data?.title) applyTitle(data.title);
				if (this.props.onChatRenamed) this.props.onChatRenamed(chatId, data?.title || title);
			})
			.catch((err) => {
				applyTitle(previous);
				error_manager("Failed to rename chat", err);
			});
	};

	renderChatRenameField = (sx = {}) => (
		<TextField
			autoFocus
			fullWidth
			size="small"
			variant="standard"
			value={this.state.renameDraft}
			inputProps={{maxLength: 120, "aria-label": "Chat name"}}
			onChange={(e) => this.setState({renameDraft: e.target.value})}
			onClick={(e) => e.stopPropagation()}
			onMouseDown={(e) => e.stopPropagation()}
			onFocus={(e) => e.target.select()}
			onKeyDown={(e) => {
				// Keep Menu typeahead / list navigation from stealing keys
				e.stopPropagation();
				if (e.key === "Enter") this.commitChatRename();
				else if (e.key === "Escape") this.cancelChatRename();
			}}
			onBlur={this.commitChatRename}
			sx={{
				"& .MuiInputBase-input": {color: CHAT_TEXT_COLOR, fontSize: "0.78rem", py: 0.25},
				"& .MuiInput-underline:before": {borderBottomColor: "rgba(255,255,255,0.3)"},
				...sx,
			}}
		/>
	);

	handleDeleteChat = () => {
		const chatId = this.state.chatId;
		if (!this.props.slide?.id || !chatId || this.state.viewingArchived) return;
		const ok = window.confirm(
			"Hide this chat from your list?\n\n"
			+ "This does NOT permanently delete the chat. "
			+ "Messages and attachments stay on the server and remain available to administrators.",
		);
		if (!ok) return;
		this.setState({chatMenuAnchor: null, loadingChat: true});
		const arch = this.includeArchivedSessions();
		archiveSlideAiChat(this.props.slide.id, chatId, {includeArchived: arch})
			.then((data) => {
				const messages = this.assignSerialSiteLabels(data.messages || []);
				notifyInfo(
					"morpho-chat-archive",
					data.message
					|| "Chat hidden. Data was not permanently deleted.",
				);
				this.setState({
					messages,
					chatId: data.chat_id,
					chatTitle: data.title || "New chat",
					chatSessions: data.sessions?.chats || [],
					viewingArchived: false,
					capturedImages: [],
					loadingChat: false,
					...this.scopeStateFrom(data),
				}, () => {
					this.stickToBottom = true;
					this.prefetchAttachmentBlobs(this.state.messages);
					this.scrollToBottom({force: true});
				});
			})
			.catch((err) => {
				this.setState({loadingChat: false});
				error_manager("Could not hide chat", err);
			});
	};

	applyJobStreamDelta = (jobId, assistantMsgId, data) => {
		const text = data?.stream_text || "";
		const liveUsage = data?.usage_live || data?.usage || null;
		if (!text && !liveUsage && data?.state !== "queued" && data?.state !== "running") return;
		this.setState((prev) => ({
			messages: (prev.messages || []).map((m) => {
				if (m.id !== assistantMsgId && m.job_id !== jobId) return m;
				if (m.status === "done" || m.status === "failed" || m.status === "cancelled") return m;
				return {
					...m,
					status: "pending",
					content: text || m.content,
					streaming: true,
					...(liveUsage && (liveUsage.tokens_in != null || liveUsage.tokens_out != null)
						? {usage: {...liveUsage, estimated: false}}
						: {}),
				};
			}),
		}), () => this.scrollToBottomIfSticky());
	};

	applyJobTerminal = (jobId, assistantMsgId, data) => {
		if (this.jobWsClosers?.[jobId]) {
			try { this.jobWsClosers[jobId].close(); } catch (_) { /* ignore */ }
			delete this.jobWsClosers[jobId];
		}
		if (this.jobPollTimers[jobId]) {
			clearInterval(this.jobPollTimers[jobId]);
			delete this.jobPollTimers[jobId];
		}
		const finish = () => {
			this.queueBusy = false;
			this.drainSendQueue();
		};
		const afterSettle = (messages, content, ok) => {
			this.prefetchAttachmentBlobs(messages);
			this.scrollToBottomIfSticky();
			if (ok && content) {
				this.maybeAutoFulfillViewRequests(content, messages)
					.catch(() => {})
					.finally(finish);
				return;
			}
			finish();
		};
		if (data.chat?.messages) {
			const msgs = this.assignSerialSiteLabels(data.chat.messages);
			const assistant = msgs.find((m) => m.id === assistantMsgId || m.job_id === jobId)
				|| [...msgs].reverse().find((m) => m.role === "assistant");
			this.setState({messages: msgs}, () => {
				afterSettle(msgs, assistant?.content || "", data.state === "done" || !data.state);
			});
			return;
		}
		const content = data.answer || data.message || data.stream_text || "";
		const ok = data.state === "done";
		this.setState((prev) => {
			const messages = (prev.messages || []).map((m) => {
				if (m.id !== assistantMsgId && m.job_id !== jobId) return m;
				return {
					...m,
					status: ok ? "done" : data.state,
					content: content || m.content,
					failed: !ok,
					streaming: false,
					thought_ms: data.thought_ms,
					...(data.usage ? {usage: data.usage} : {}),
					...(data.timings ? {timings: data.timings} : {}),
					...(data.model_slug ? {model: `cursor_cli:${data.model_slug}`} : {}),
				};
			});
			return {messages};
		}, () => afterSettle(this.state.messages, content, ok));
	};

	/** Collect site attachments from chat (newest first) for view-request resolution. */
	collectChatAttachments = (messages, {slideId = null} = {}) => {
		const out = [];
		const seen = new Set();
		[...(messages || [])].reverse().forEach((m) => {
			(m.attachments || []).forEach((a) => {
				if (slideId && String(this.attSlideId(a)) !== String(slideId)) return;
				const lab = String(a?.label || "").toUpperCase();
				const key = lab || a?.file_id || a?.preview_url || JSON.stringify(a?.map_state || {});
				if (seen.has(key)) return;
				seen.add(key);
				out.push(a);
			});
		});
		return out;
	};

	buildAutoViewFollowUpQuestion = (labeledSites, skipped, depthRound, depthMax) => {
		const sources = [];
		const parts = (labeledSites || []).map((s) => {
			const r = s.viewRequest || {};
			const res = s.viewResolved || {};
			const why = r.reason ? ` — ${r.reason}` : "";
			const from = res.sourceSite ? ` from Image ${res.sourceSite}` : "";
			if (res.sourceSite && !sources.includes(res.sourceSite)) sources.push(res.sourceSite);
			const zooms = res.zooms || s.available_zooms || s.scaleContext?.available_zooms || null;
			const change = zoomChangeText(res.sourceZ, res.z, zooms);
			const zoom = change ? ` ${change}` : "";
			const askedMag = magLabelForZ(res.requestedZ, zooms);
			const capped = res.zCapped
				? ` (asked ${askedMag || `z${res.requestedZ}`}; capped at +2 steps per round)`
				: "";
			return `Image ${s.label} = ${r.id || s.viewRequestId || "request"} ${r.intent || "view"}${from}${zoom}${capped}${why}`;
		});
		const skipText = (skipped || []).map((k) => {
			const id = k.request?.id || "request";
			if (k.reason === "duplicate") return `${id} skipped (same view as Image ${k.duplicateOf})`;
			if (k.reason === "mostly_empty") return `${id} skipped (field was mostly glass)`;
			if (k.reason === "other_part") return `${k.id || id} deferred (on ${k.part}; ask again for it after this round)`;
			return `${id} skipped (${k.reason})`;
		});
		const labels = (labeledSites || []).map((s) => s.label);
		const newList = labels.length > 1 ? `Images ${labels[0]}–${labels[labels.length - 1]}` : `Image ${labels[0] || ""}`;
		const parent = sources.length ? `Image${sources.length > 1 ? "s" : ""} ${sources.join(", ")}` : "the earlier images";
		const moreRounds = depthRound < depthMax;
		const nextHint = moreRounds
			? "If more FOVs are still needed — including on tissue fragments not examined yet — emit ```ai-view-request``` and/or closer_look features so auto-capture can continue."
			: "This is the last auto-capture round: finish the OVERALL assessment across all images so far, and name any tissue fragments you did not examine.";
		return (
			`Auto-captured follow-up FOVs (round ${depthRound}/${depthMax}): `
			+ `${parts.join("; ")}. `
			+ (skipText.length ? `${skipText.join("; ")}. ` : "")
			+ `This continues your assessment of ${parent} in the same thread — do not restart. `
			+ `State what ${newList} confirms, changes or adds, then give one updated impression covering every image so far. ${nextHint}`
		);
	};

	/** Slide a view request targets: explicit part/slide, else the part its source image came from. */
	requestTargetSlide = (req, attachments) => {
		const current = this.props.slide?.id ? String(this.props.slide.id) : null;
		if (!this.isCaseChat()) return current;
		const parts = this.state.chatCase?.parts || [];
		if (req?.targetSlideId && parts.some((p) => String(p.slide_id) === String(req.targetSlideId))) {
			return String(req.targetSlideId);
		}
		if (req?.targetPart) {
			const hit = parts.find((p) => p.part === req.targetPart);
			if (hit) return String(hit.slide_id);
		}
		if (req?.sourceSite) {
			const L = String(req.sourceSite).toUpperCase();
			const att = (attachments || []).find((a) => String(a?.label || "").toUpperCase() === L);
			if (att) return String(this.attSlideId(att));
		}
		return current;
	};

	maybeAutoFulfillViewRequests = async (content, messages) => {
		if (this.isDashboard() || !this.props.slidemap) return;
		const depthMax = Number(this.state.autoViewDepthMax) || 0;
		if (depthMax < 1) return;
		if (this.state.autoCapturing) return;
		const depthNow = Number(this.state.autoViewDepth) || 0;
		if (depthNow >= depthMax) return;
		// Prefer explicit ai-view-request; else synthesize from closer_look ROIs.
		const allReqs = synthesizeViewRequestsFromContent(content).slice(0, MAX_VIEW_REQUESTS_PER_ROUND);
		if (!allReqs.length) {
			if (countUnparsedViewRequestBlocks(content) > 0) {
				notifyInfo("Morpho Chat", "The model's view request could not be read, so nothing was auto-captured");
			}
			return;
		}
		const sourceMsg = [...(messages || [])].reverse().find((m) => m.role === "assistant");
		const everyAtt = this.collectChatAttachments(messages);
		const current = String(this.props.slide.id);
		const byTarget = {};
		allReqs.forEach((r) => {
			const t = this.requestTargetSlide(r, everyAtt) || current;
			(byTarget[t] = byTarget[t] || []).push(r);
		});
		const skipped = [];
		let reqs = byTarget[current] || [];
		const others = Object.keys(byTarget).filter((t) => t !== current);
		if (!reqs.length && others.length) {
			// Everything asked is on another part → switch part and continue there.
			const target = others[0];
			this.handOffToPart(target, {
				mode: "auto",
				requests: byTarget[target],
				depthNow,
				depthMax,
				sourceMsgId: sourceMsg?.id,
			});
			return;
		}
		others.forEach((t) => byTarget[t].forEach((r) => skipped.push({
			id: r.id,
			reason: "other_part",
			part: this.partForSlide(t) || "another part",
		})));
		await this.runAutoViewRound(reqs, {messages, depthNow, depthMax, skipped, sourceMsgId: sourceMsg?.id});
	};

	runAutoViewRound = async (reqs, {messages, depthNow, depthMax, skipped = [], sourceMsgId = null}) => {
		const depthRound = depthNow + 1;
		const attachments = this.collectChatAttachments(messages, {
			slideId: this.isCaseChat() ? this.props.slide.id : null,
		});
		const prevMap = this.currentMapState();
		this.setState({
			autoCapturing: true,
			autoCaptureProgress: {done: 0, total: reqs.length, phase: "navigate"},
		});
		try {
			const rawSites = await fulfillViewRequests({
				skipped,
				slidemap: this.props.slidemap,
				slide: this.props.slide,
				displaySetting: this.props.displaySetting,
				activeMapId: this.props.activeMapId,
				requests: reqs,
				attachments,
				currentMapState: prevMap,
				extension: this.props.extension || ScreenshotExtensions.JPEG,
				maxRequests: MAX_VIEW_REQUESTS_PER_ROUND,
				onProgress: ({index, phase, intent}) => {
					this.setState({
						autoCaptureProgress: {
							done: index,
							total: reqs.length,
							phase,
							intent,
						},
					});
				},
			});
			if (!rawSites.length) {
				if (sourceMsgId && this.isCaseChat() && !skipped.some((k) => k.reason === "duplicate")) {
					this.showHandoffCard(sourceMsgId, {
						targetSlideId: this.props.slide.id,
						mapState: this.currentMapState(),
						reason: "capture_failed",
						requests: reqs,
					});
				}
				const allDup = skipped.length && skipped.every((k) => k.reason === "duplicate");
				notifyInfo(
					"Morpho Chat",
					allDup
						? `Requested view${skipped.length > 1 ? "s were" : " was"} already captured (Image ${skipped.map((k) => k.duplicateOf).join(", ")})`
						: "View request could not be captured",
				);
				return;
			}
			const labeled = [];
			const reserved = new Set();
			rawSites.forEach((s) => {
				let label = null;
				const used = new Set(reserved);
				(this.state.messages || []).forEach((m) => {
					(m.attachments || []).forEach((a) => {
						if (a?.label) used.add(String(a.label).toUpperCase());
					});
				});
				labeled.forEach((x) => used.add(x.label));
				for (let i = 0; i < 200; i += 1) {
					const lab = siteLabelAt(i);
					if (!used.has(lab)) {
						label = lab;
						break;
					}
				}
				label = label || siteLabelAt(Date.now() % 1000);
				reserved.add(label);
				labeled.push({...s, label});
			});
			const question = this.buildAutoViewFollowUpQuestion(labeled, skipped, depthRound, depthMax);
			// Bump depth before enqueue so the next assistant reply sees the new counter.
			await new Promise((resolve) => {
				this.setState(
					{autoViewDepth: depthRound, autoCapturing: false, autoCaptureProgress: null},
					resolve,
				);
			});
			await this.enqueueQuestion({
				question,
				sites: labeled,
				annoRequired: Boolean(this.state.annoRequired),
				isAutoFollowUp: true,
			});
		} catch (err) {
			error_manager("Auto-capture failed", err);
		} finally {
			this.setState({autoCapturing: false, autoCaptureProgress: null});
		}
	};

	/**
	 * Cross-part continuation: store the pending work, then open the target part in this tab
	 * with the same (case-linked) chat. Falls back to an inline card when that is not possible.
	 */
	handOffToPart = (targetSlideId, {mode, requests = [], depthNow = 0, depthMax = 0, sourceMsgId = null, mapState = null}) => {
		const part = this.partForSlide(targetSlideId) || "the other part";
		const source = requests[0]?.sourceSite
			? this.findAttachmentByLabel(requests[0].sourceSite) : null;
		const startState = mapState || source?.map_state || null;
		const busy = mode !== "auto" && Boolean(this.state.loading || this.state.capturing || this.queueBusy);
		if (!this.isCaseChat() || !this.state.chatId || this.isDashboard() || busy) {
			if (sourceMsgId) {
				this.showHandoffCard(sourceMsgId, {targetSlideId, mapState: startState, reason: "not_navigable", requests});
			}
			return false;
		}
		const ok = writePendingHandoff({
			mode,
			chat_id: this.state.chatId,
			target_slide_id: String(targetSlideId),
			requests,
			depth_now: depthNow,
			depth_max: depthMax,
			source_msg_id: sourceMsgId,
		});
		if (!ok) {
			if (sourceMsgId) this.showHandoffCard(sourceMsgId, {targetSlideId, mapState: startState, reason: "storage", requests});
			return false;
		}
		notifyInfo("Morpho Chat", `Opening ${part} to continue…`);
		this.navigateToPart(targetSlideId, {mapState: startState});
		return true;
	};

	navigateToPart = (slideId, {mapState = null, newTab = false} = {}) => {
		const url = gammaUrlForMapState(slideId, mapState, {openChat: true, chatId: this.state.chatId});
		if (!url) return;
		const full = getStateAwareUrl(url);
		if (newTab) window.open(full, "_blank", "noopener");
		else window.location.assign(full);
	};

	showHandoffCard = (msgId, card) => {
		if (!msgId) return;
		this.setState((prev) => ({handoffCards: {...prev.handoffCards, [msgId]: card}}));
	};

	dismissHandoffCard = (msgId) => {
		this.setState((prev) => {
			const next = {...prev.handoffCards};
			delete next[msgId];
			return {handoffCards: next};
		});
	};

	waitForSlidemap = async (timeoutMs = 15000) => {
		const t0 = Date.now();
		while (!this.props.slidemap && Date.now() - t0 < timeoutMs) {
			await new Promise((r) => setTimeout(r, 200));
		}
		return this.props.slidemap || null;
	};

	/** On the target part: finish the work a cross-part request left in sessionStorage. */
	maybeConsumeHandoff = async () => {
		if (this.consumingHandoff || this.isDashboard()) return;
		const h = readPendingHandoff();
		if (!h || String(h.target_slide_id) !== String(this.props.slide?.id)) return;
		if (h.chat_id && this.state.chatId && h.chat_id !== this.state.chatId) {
			clearPendingHandoff();
			return;
		}
		this.consumingHandoff = true;
		clearPendingHandoff();
		try {
			const slidemap = await this.waitForSlidemap();
			if (!slidemap) throw new Error("viewer not ready");
			await waitForMapIdle(slidemap, {timeoutMs: 8000});
			const reqs = Array.isArray(h.requests) ? h.requests : [];
			if (h.mode === "auto" && reqs.length) {
				await new Promise((resolve) => this.setState({autoViewDepth: Number(h.depth_now) || 0}, resolve));
				await this.runAutoViewRound(reqs, {
					messages: this.state.messages,
					depthNow: Number(h.depth_now) || 0,
					depthMax: Number(h.depth_max) || 0,
					sourceMsgId: h.source_msg_id,
				});
			} else if (h.mode === "goto" && reqs[0]) {
				await this.goToViewRequest(reqs[0]);
			} else if (h.mode === "capture" && reqs[0]) {
				await this.manualCaptureViewRequest(reqs[0]);
			}
		} catch (err) {
			this.showHandoffCard(h.source_msg_id, {
				targetSlideId: this.props.slide?.id,
				mapState: this.currentMapState(),
				reason: "capture_failed",
				requests: h.requests || [],
			});
		} finally {
			this.consumingHandoff = false;
		}
	};

	renderHandoffCard = (msg) => {
		const card = this.state.handoffCards?.[msg.id];
		if (!card) return null;
		const part = this.partForSlide(card.targetSlideId) || "the other part";
		const here = String(card.targetSlideId) === String(this.props.slide?.id);
		const btnSx = {textTransform: "none", fontSize: "0.72rem", py: 0.1};
		return (
			<Box
				data-testid="morpho-handoff-card"
				sx={{
					mt: 0.75, p: 0.75, borderRadius: 1,
					border: "1px solid rgba(158,203,255,0.45)", backgroundColor: "rgba(158,203,255,0.08)",
				}}
			>
				<Typography variant="caption" sx={{color: CHAT_TEXT_COLOR, display: "block", fontWeight: 600}}>
					{here ? `Couldn't capture automatically on ${part}` : `Open ${part} to continue`}
				</Typography>
				<Typography variant="caption" sx={{color: CHAT_TEXT_COLOR, opacity: 0.75, display: "block", mb: 0.5}}>
					{here
						? "Position the view on the region the model asked for, then capture it into the tray and send."
						: "The model asked to look further at an image from another part of this case."}
				</Typography>
				<Stack direction="row" spacing={0.75} flexWrap="wrap">
					{here ? (
						<Button size="small" variant="outlined" color="secondary" sx={btnSx}
							onClick={() => { this.dismissHandoffCard(msg.id); this.handleCaptureImage(); }}>
							Capture this view
						</Button>
					) : (
						<>
							<Button size="small" variant="outlined" color="secondary" sx={btnSx}
								onClick={() => {
									this.dismissHandoffCard(msg.id);
									const sent = card.requests?.length && this.handOffToPart(card.targetSlideId, {
										mode: "goto",
										requests: card.requests,
										mapState: card.mapState,
									});
									if (!sent) this.navigateToPart(card.targetSlideId, {mapState: card.mapState});
								}}>
								Open {part}
							</Button>
							<Button size="small" color="secondary" sx={btnSx}
								onClick={() => this.navigateToPart(card.targetSlideId, {mapState: card.mapState, newTab: true})}>
								New tab ↗
							</Button>
						</>
					)}
					<Button size="small" color="secondary" sx={{...btnSx, opacity: 0.7}} onClick={() => this.dismissHandoffCard(msg.id)}>
						Dismiss
					</Button>
				</Stack>
			</Box>
		);
	};

	goToViewRequest = async (req, msgAttachments, sourceMsgId = null) => {
		if (this.isDashboard() || !this.props.slidemap) {
			notifyInfo("Morpho Chat", "Open the slide in the viewer to navigate");
			return;
		}
		const target = this.requestTargetSlide(req, this.collectChatAttachments(this.state.messages));
		if (target && target !== String(this.props.slide.id)) {
			this.handOffToPart(target, {mode: "goto", requests: [req], sourceMsgId});
			return;
		}
		const attachments = this.isCaseChat()
			? this.collectChatAttachments(this.state.messages, {slideId: this.props.slide.id})
			: msgAttachments?.length
				? msgAttachments
				: this.collectChatAttachments(this.state.messages);
		const ms = resolveViewRequestMapState(req, attachments, {
			currentMapState: this.currentMapState(),
			slidemap: this.props.slidemap,
			slide: this.props.slide,
		});
		if (!ms) {
			notifyInfo("Morpho Chat", "Could not resolve that view request");
			return;
		}
		await navigateToMapStateVerified(this.props.slidemap, ms, {timeoutMs: 6000});
	};

	manualCaptureViewRequest = async (req, msgAttachments, sourceMsgId = null) => {
		if (this.isDashboard() || !this.props.slidemap) {
			notifyInfo("Morpho Chat", "Open the slide in the viewer to capture");
			return;
		}
		const target = this.requestTargetSlide(req, this.collectChatAttachments(this.state.messages));
		if (target && target !== String(this.props.slide.id)) {
			this.handOffToPart(target, {mode: "capture", requests: [req], sourceMsgId});
			return;
		}
		const attachments = this.isCaseChat()
			? this.collectChatAttachments(this.state.messages, {slideId: this.props.slide.id})
			: msgAttachments?.length
				? msgAttachments
				: this.collectChatAttachments(this.state.messages);
		this.setState({capturing: true});
		try {
			const sites = await fulfillViewRequests({
				slidemap: this.props.slidemap,
				slide: this.props.slide,
				displaySetting: this.props.displaySetting,
				activeMapId: this.props.activeMapId,
				requests: [req],
				attachments,
				currentMapState: this.currentMapState(),
				extension: this.props.extension || ScreenshotExtensions.JPEG,
				maxRequests: 1,
				skipDuplicates: false,
			});
			if (!sites[0]) {
				notifyInfo("Morpho Chat", "Capture failed");
				return;
			}
			const label = this.nextChatSiteLabel();
			const site = {
				...sites[0],
				label,
				id: `cap-${Date.now()}-${label}`,
			};
			this.setState((prev) => ({
				capturedImages: [...(prev.capturedImages || []), site],
				capturing: false,
			}));
		} catch (err) {
			this.setState({capturing: false});
			error_manager("Capture failed", err);
		}
	};

	setAutoViewDepthMax = (n) => {
		const v = clampAutoViewDepthMax(n);
		this.setState({autoViewDepthMax: v, autoViewDepth: 0});
		writeLocalSlideAiPrefs({autoViewDepthMax: v});
	};

	pollJobUntilDone = (jobId, assistantMsgId) => {
		if (this.jobPollTimers[jobId] || this.jobWsClosers?.[jobId]) return;
		if (!this.jobWsClosers) this.jobWsClosers = {};
		let settled = false;
		const settle = (data) => {
			if (settled) return;
			settled = true;
			this.applyJobTerminal(jobId, assistantMsgId, data);
		};

		// Prefer Daphne WebSocket streaming; fall back to HTTP poll.
		const ws = subscribeMorphoChatJob({
			jobId,
			slideId: this.props.slide?.id,
			onDelta: (data) => {
				if (settled) return;
				this.applyJobStreamDelta(jobId, assistantMsgId, data);
			},
			onDone: (data) => settle(data),
			onError: () => {
				// Keep HTTP poll running as fallback
			},
		});
		this.jobWsClosers[jobId] = ws;

		const tick = () => {
			if (!this.props.slide?.id || settled) return;
			fetchSlideAiJob(jobId, this.props.slide.id)
				.then((data) => {
					if (!data?.ok || settled) return;
					if (data.state === "running" || data.state === "queued") {
						this.applyJobStreamDelta(jobId, assistantMsgId, data);
						return;
					}
					if (!["done", "failed", "cancelled"].includes(data.state)) return;
					settle(data);
				})
				.catch(() => {});
		};
		this.jobPollTimers[jobId] = setInterval(tick, 800);
		tick();
	};

	/** Near-bottom threshold (px) for sticky autoscroll during streaming. */
	isChatNearBottom = (threshold = 96) => {
		const el = this.listRef.current;
		if (!el) return true;
		return (el.scrollHeight - el.scrollTop - el.clientHeight) <= threshold;
	};

	onChatListScroll = () => {
		this.stickToBottom = this.isChatNearBottom();
	};

	scrollToBottom = ({force = false} = {}) => {
		const el = this.listRef.current;
		if (!el) return;
		if (!force && !this.stickToBottom) return;
		el.scrollTop = el.scrollHeight;
		this.stickToBottom = true;
	};

	/** Autoscroll only while the user is pinned to the bottom. */
	scrollToBottomIfSticky = () => {
		if (this.stickToBottom) this.scrollToBottom({force: true});
	};

	onModelChange = (e) => {
		const provider = e.target.value;
		const m = this.state.models.find((x) => x.provider === provider);
		const modelId = m?.model_id || "";
		this.setState({provider, modelId}, () => {
			this.persistProviderModel(provider, modelId);
			if (CURSOR_PROVIDERS.includes(provider)) {
				this.refreshCursorStatus();
				this.refreshCursorModels();
			}
		});
	};

	openModelMenu = (e) => {
		this.setState({modelMenuAnchor: e.currentTarget, modelSubmenu: null, submenuAnchor: null, modelSearch: ""}, () => {
			if (this.isCursorProvider()) {
				this.refreshCursorStatus();
				if (this.state.cursor?.state === "connected") this.refreshCursorModels();
			}
		});
	};

	closeModelMenu = () => this.setState({
		modelMenuAnchor: null, modelSubmenu: null, submenuAnchor: null, modelSearch: "",
	});

	openSubmenu = (key) => (e) => {
		this.setState({modelSubmenu: key, submenuAnchor: e.currentTarget});
	};

	filteredCursorModels = () => {
		const q = (this.state.modelSearch || "").trim().toLowerCase();
		const list = this.state.cursorModels || [];
		if (!q) return list;
		return list.filter((m) => {
			const label = this.cleanModelLabel(m.label, m.slug).toLowerCase();
			return label.includes(q) || (m.slug || "").toLowerCase().includes(q);
		});
	};

	menuPaperSx = {
		bgcolor: "#1e1e1e",
		color: "#fff",
		border: "1px solid rgba(255,255,255,0.12)",
		borderRadius: 1.5,
		minWidth: 220,
		py: 0.5,
	};

	renderModelMenus = () => {
		const rootOpen = Boolean(this.state.modelMenuAnchor);
		const sub = this.state.modelSubmenu;
		const isCursor = this.isCursorProvider();
		const st = this.state.cursor?.state || "disconnected";
		const connected = isCursor && st === "connected";
		const optLabel = AUTO_OPTIMIZE.find((o) => o.id === this.state.cursorOptimizeFor)?.label || "Balance";
		const modelRight = connected
			? ((this.state.cursorFamily || "auto") === "auto" ? "Auto" : this.cursorSelectionLabel())
			: (isCursor ? "Connect" : this.cursorSelectionLabel());

		return (
			<>
				<Menu
					anchorEl={this.state.modelMenuAnchor}
					open={rootOpen}
					onClose={this.closeModelMenu}
					anchorOrigin={{vertical: "top", horizontal: "right"}}
					transformOrigin={{vertical: "bottom", horizontal: "right"}}
					PaperProps={{sx: this.menuPaperSx}}
				>
					{isCursor && connected && (
						<MenuItem
							onClick={this.openSubmenu("optimize")}
							selected={sub === "optimize"}
							sx={{minWidth: 240}}
						>
							<ListItemText primary="Optimize For" />
							<Typography variant="body2" sx={{opacity: 0.7, mr: 0.5}}>
								{(this.state.cursorFamily || "auto") === "auto" ? optLabel : "—"}
							</Typography>
							<ChevronRightIcon fontSize="small" sx={{opacity: 0.6}} />
						</MenuItem>
					)}
					<MenuItem
						onClick={this.openSubmenu("model")}
						selected={sub === "model"}
						sx={{minWidth: 240}}
					>
						<ListItemText primary="Model" />
						<Typography variant="body2" sx={{opacity: 0.7, mr: 0.5, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis"}}>
							{modelRight}
						</Typography>
						<ChevronRightIcon fontSize="small" sx={{opacity: 0.6}} />
					</MenuItem>
					<MenuItem
						onClick={this.openSubmenu("provider")}
						selected={sub === "provider"}
						sx={{minWidth: 240}}
					>
						<ListItemText primary="Provider" />
						<Typography variant="body2" sx={{opacity: 0.7, mr: 0.5}}>
							{(this.state.models.find((m) => m.provider === this.state.provider)?.label || this.state.provider || "")
								.replace(/\s*\(.*\)$/, "")}
						</Typography>
						<ChevronRightIcon fontSize="small" sx={{opacity: 0.6}} />
					</MenuItem>
					{isSuperuser() && (
						<>
							<Divider sx={{borderColor: "rgba(255,255,255,0.1)", my: 0.5}} />
							<MenuItem onClick={this.openSystemPromptEditor} sx={{minWidth: 240}}>
								<ListItemIcon sx={{color: "#fff", minWidth: 32}}>
									<EditNoteOutlinedIcon fontSize="small" />
								</ListItemIcon>
								<ListItemText primary="Edit system prompt" />
							</MenuItem>
						</>
					)}
					{isCursor && (
						<>
							<Divider sx={{borderColor: "rgba(255,255,255,0.1)", my: 0.5}} />
							{connected ? (
								<>
									<MenuItem disabled sx={{opacity: "0.75 !important", fontSize: "0.8rem"}}>
										{this.state.cursor.email}
										{this.state.cursor.team ? ` · ${this.state.cursor.team}` : ""}
									</MenuItem>
									<MenuItem
										component="a"
										href={CURSOR_USAGE_URL}
										target="_blank"
										rel="noopener noreferrer"
										onClick={this.closeModelMenu}
									>
										View Cursor usage ↗
									</MenuItem>
									<MenuItem onClick={() => { this.closeModelMenu(); this.handleCursorDisconnect(); }}>
										<ListItemIcon sx={{color: "#fff", minWidth: 32}}><LogoutIcon fontSize="small" /></ListItemIcon>
										<ListItemText primary="Sign out" />
									</MenuItem>
								</>
							) : (
								<MenuItem
									onClick={() => { this.closeModelMenu(); this.handleCursorConnect(); }}
									disabled={st === "runner_down"}
								>
									<ListItemIcon sx={{color: "#fff", minWidth: 32}}><LoginIcon fontSize="small" /></ListItemIcon>
									<ListItemText primary={st === "runner_down" ? "Runner offline" : "Sign in with Cursor"} />
								</MenuItem>
							)}
						</>
					)}
				</Menu>

				{/* Optimize For submenu */}
				<Menu
					anchorEl={this.state.submenuAnchor}
					open={sub === "optimize"}
					onClose={() => this.setState({modelSubmenu: null, submenuAnchor: null})}
					anchorOrigin={{vertical: "top", horizontal: "right"}}
					transformOrigin={{vertical: "top", horizontal: "left"}}
					PaperProps={{sx: {...this.menuPaperSx, minWidth: 160}}}
				>
					{AUTO_OPTIMIZE.map((o) => (
						<MenuItem
							key={o.id}
							onClick={() => {
								this.persistCursorSelection({family: "auto", optimizeFor: o.id});
								this.setState({modelSubmenu: null, submenuAnchor: null});
							}}
						>
							<ListItemText primary={o.label} />
							{this.state.cursorOptimizeFor === o.id && (this.state.cursorFamily || "auto") === "auto" && (
								<CheckIcon fontSize="small" sx={{ml: 1}} />
							)}
						</MenuItem>
					))}
				</Menu>

				{/* Model submenu (+ search) */}
				<Menu
					anchorEl={this.state.submenuAnchor}
					open={sub === "model"}
					onClose={() => this.setState({modelSubmenu: null, submenuAnchor: null, modelSearch: ""})}
					anchorOrigin={{vertical: "top", horizontal: "right"}}
					transformOrigin={{vertical: "top", horizontal: "left"}}
					PaperProps={{sx: {...this.menuPaperSx, minWidth: 280, maxHeight: 420}}}
					MenuListProps={{dense: true}}
				>
					{connected ? (
						<>
							<Box sx={{px: 1.5, py: 1}} onKeyDown={(e) => e.stopPropagation()}>
								<TextField
									size="small"
									fullWidth
									autoFocus
									placeholder="Search models"
									value={this.state.modelSearch}
									onChange={(e) => this.setState({modelSearch: e.target.value})}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon sx={{color: "rgba(255,255,255,0.5)", fontSize: 18}} />
											</InputAdornment>
										),
									}}
									sx={{
										"& .MuiOutlinedInput-root": {
											color: "#fff",
											bgcolor: "rgba(255,255,255,0.06)",
											"& fieldset": {borderColor: "rgba(255,255,255,0.15)"},
										},
									}}
								/>
							</Box>
							<MenuItem
								onClick={() => {
									this.persistCursorSelection({family: "auto"});
									this.closeModelMenu();
								}}
							>
								<ListItemText primary={this.autoModeLabel()} />
								{(this.state.cursorFamily || "auto") === "auto" && <CheckIcon fontSize="small" />}
							</MenuItem>
							<Divider sx={{borderColor: "rgba(255,255,255,0.1)"}} />
							{this.filteredCursorModels().map((m) => {
								const label = this.cleanModelLabel(m.label, m.slug) + (m.no_zdr ? " (no ZDR)" : "");
								const selected = this.state.modelId === m.slug;
								return (
									<MenuItem
										key={m.slug}
										onClick={() => {
											this.persistCursorSelection({family: m.family || m.slug, modelSlug: m.slug});
											this.closeModelMenu();
										}}
									>
										<ListItemText primary={label} primaryTypographyProps={{noWrap: true}} />
										{selected && <CheckIcon fontSize="small" sx={{ml: 1}} />}
									</MenuItem>
								);
							})}
							{!this.filteredCursorModels().length && (
								<MenuItem disabled>No models match</MenuItem>
							)}
							<Divider sx={{borderColor: "rgba(255,255,255,0.1)"}} />
							<MenuItem onClick={() => this.refreshCursorModels(true)}>
								Refresh models
							</MenuItem>
						</>
					) : isCursor ? (
						<MenuItem onClick={() => { this.closeModelMenu(); this.handleCursorConnect(); }}>
							Sign in with Cursor to see models
						</MenuItem>
					) : (
						(this.state.models || []).filter((m) => m.provider === this.state.provider).map((m) => (
							<MenuItem key={m.provider} selected>
								<ListItemText primary={m.label} />
								<CheckIcon fontSize="small" />
							</MenuItem>
						))
					)}
				</Menu>

				{/* Provider submenu */}
				<Menu
					anchorEl={this.state.submenuAnchor}
					open={sub === "provider"}
					onClose={() => this.setState({modelSubmenu: null, submenuAnchor: null})}
					anchorOrigin={{vertical: "top", horizontal: "right"}}
					transformOrigin={{vertical: "top", horizontal: "left"}}
					PaperProps={{sx: {...this.menuPaperSx, minWidth: 200}}}
				>
					{(this.state.models.length ? this.state.models : [
						{provider: "gemini", label: "Gemini"},
						{provider: "xai", label: "xAI"},
						{provider: "cursor_cli", label: "Cursor (your account)"},
					]).map((m) => (
						<MenuItem
							key={m.provider}
							onClick={() => {
								this.onModelChange({target: {value: m.provider}});
								this.setState({modelSubmenu: null, submenuAnchor: null});
							}}
						>
							<ListItemText primary={(m.label || m.provider).replace(/\s*\(.*\)$/, "")} />
							{this.state.provider === m.provider && <CheckIcon fontSize="small" />}
						</MenuItem>
					))}
				</Menu>

				{/* Sign-in pending QR dialog-ish overlay via Menu isn't ideal — keep connect flow toast */}
				{(st === "login_pending" || this.state.cursorConnecting) && this.state.cursorQr && (
					<Dialog open onClose={this.handleCursorCancelConnect}>
						<DialogContent sx={{bgcolor: "#1e1e1e", color: "#fff", textAlign: "center"}}>
							<Typography variant="body2" sx={{mb: 1}}>Scan or open to sign in with Cursor</Typography>
							<img src={this.state.cursorQr} alt="Cursor sign-in QR" style={{width: 180, height: 180, background: "#fff"}} />
							<Stack direction="row" spacing={1} justifyContent="center" sx={{mt: 1.5}}>
								{this.state.cursorLoginUrl && (
									<Button size="small" variant="contained" color="secondary"
										onClick={() => window.open(this.state.cursorLoginUrl, "_blank", "noopener")}>
										Open link
									</Button>
								)}
								<Button size="small" color="secondary" onClick={this.handleCursorCancelConnect}>Cancel</Button>
							</Stack>
						</DialogContent>
					</Dialog>
				)}

				{isSuperuser() && (
					<Dialog
						open={!!this.state.systemPromptOpen}
						onClose={this.closeSystemPromptEditor}
						fullWidth
						maxWidth="md"
					>
						<DialogTitle sx={{bgcolor: "#1e1e1e", color: "#fff", pb: 1}}>
							Edit Morpho Chat system prompt
							<Typography variant="caption" display="block" sx={{opacity: 0.65, mt: 0.5, fontWeight: 400}}>
								{this.state.systemPromptLoading
									? "Loading…"
									: this.state.systemPromptHasOverride
										? "Using custom override (superuser)"
										: "Using bundled default"}
							</Typography>
						</DialogTitle>
						<DialogContent sx={{bgcolor: "#1e1e1e", color: "#fff", pt: "8px !important"}}>
							{this.state.systemPromptLoading ? (
								<Box sx={{display: "flex", justifyContent: "center", py: 4}}>
									<CircularProgress size={28} color="inherit" />
								</Box>
							) : (
								<TextField
									multiline
									fullWidth
									minRows={16}
									maxRows={28}
									value={this.state.systemPromptDraft}
									onChange={(e) => this.setState({systemPromptDraft: e.target.value})}
									disabled={this.state.systemPromptBusy}
									sx={{
										"& .MuiOutlinedInput-root": {
											color: "#fff",
											fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
											fontSize: "0.8rem",
											bgcolor: "rgba(255,255,255,0.04)",
											"& fieldset": {borderColor: "rgba(255,255,255,0.18)"},
										},
									}}
								/>
							)}
						</DialogContent>
						<DialogActions sx={{bgcolor: "#1e1e1e", px: 3, pb: 2}}>
							<Button
								size="small"
								color="secondary"
								disabled={this.state.systemPromptBusy || this.state.systemPromptLoading || !this.state.systemPromptHasOverride}
								onClick={this.resetSystemPromptEditor}
							>
								Reset to bundled
							</Button>
							<Box sx={{flex: 1}} />
							<Button
								size="small"
								color="secondary"
								disabled={this.state.systemPromptBusy}
								onClick={this.closeSystemPromptEditor}
							>
								Cancel
							</Button>
							<Button
								size="small"
								variant="contained"
								color="secondary"
								disabled={this.state.systemPromptBusy || this.state.systemPromptLoading}
								onClick={this.saveSystemPromptEditor}
							>
								{this.state.systemPromptBusy ? "Saving…" : "Save"}
							</Button>
						</DialogActions>
					</Dialog>
				)}
			</>
		);
	};

	currentMapState = () => {
		const live = readLiveMapState(this.props.slidemap);
		if (live) return {x: live.x, y: live.y, z: live.z, r: live.r};
		return {
			x: this.props.x,
			y: this.props.y,
			z: this.props.z,
			r: this.props.r,
		};
	};

	currentExtent = () => {
		try {
			const map = this.props.slidemap;
			if (!map?.getView || !map.getSize) return null;
			const size = map.getSize();
			if (!size) return null;
			const ext = map.getView().calculateExtent(size);
			if (!ext || ext.length < 4) return null;
			return [ext[0], ext[1], ext[2], ext[3]];
		} catch (e) {
			return null;
		}
	};

	isDashboard = () => this.props.variant === "dashboard";

	/** Capture the live viewport as a chat site (null when capture fails). */
	captureCurrentSite = async (label) => {
		const mapState = this.currentMapState();
		const visibleCanvasBox = readVisibleCanvasBox(this.props.slidemap);
		const capture = await captureAiViewportScreenshot({
			slide: this.props.slide,
			slidemap: this.props.slidemap,
			activeMapId: this.props.activeMapId,
			mapState,
			displaySetting: this.props.displaySetting,
			extension: this.props.extension || ScreenshotExtensions.JPEG,
		});
		if (!capture?.blob && !capture?.previewUrl) return null;
		if (capture.previewUrl) {
			this.blobUrls[capture.previewUrl] = capture.previewUrl;
		}
		return {
			id: `capture-${Date.now()}-${label}`,
			label,
			blob: capture.blob || null,
			fileId: capture.fileId || null,
			previewUrl: capture.previewUrl || null,
			urlSafe: capture.urlSafe || null,
			mapState: capture.mapState || mapState,
			// Prefer extent captured with the bitmap (same FOV as image_size).
			extent: capture.extent || this.currentExtent(),
			imageSize: capture.imageSize || null,
			viewportCssSize: capture.viewportCssSize || null,
			canvasSize: capture.viewportCssSize || capture.imageSize || null,
			slideBounds: capture.slideBounds || null,
			scaleContext: capture.scaleContext || null,
			visibleCanvasBox,
		};
	};

	handleCaptureImage = async () => {
		if (!this.props.slide || this.state.capturing || this.state.loading) return;
		if ((this.state.capturedImages || []).length >= MAX_AI_SITES) {
			error_manager(`At most ${MAX_AI_SITES} images can be captured`, null);
			return;
		}
		this.setState({capturing: true});
		try {
			const site = await this.captureCurrentSite(this.nextChatSiteLabel());
			if (!site) {
				error_manager("Could not capture image", null);
				this.setState({capturing: false});
				return;
			}
			this.setState((prev) => ({capturedImages: [...prev.capturedImages, site], capturing: false}));
		} catch (err) {
			this.setState({capturing: false});
			error_manager("Capture image failed", err);
		}
	};

	removeCapturedImage = (id) => {
		this.setState((prev) => {
			// Keep serial labels stable — do not renumber remaining captures.
			const next = (prev.capturedImages || []).filter((s) => s.id !== id);
			const removed = (prev.capturedImages || []).find((s) => s.id === id);
			if (removed?.previewUrl) {
				try { URL.revokeObjectURL(removed.previewUrl); } catch (e) { /* ignore */ }
			}
			return {capturedImages: next};
		});
	};

	buildOptimisticUserMessage = (question, sites, annoRequired = false, fullAnalysis = true) => {
		const attachments = (sites || []).map((s) => {
			const sc = s.scaleContext || s.scale_context || null;
			return {
				type: "viewport_screenshot",
				label: s.label,
				preview_url: s.previewUrl || null,
				url_safe: s.urlSafe || null,
				file_id: s.fileId || null,
				map_state: s.mapState || s.map_state,
				extent: s.extent || null,
				image_size: s.imageSize || s.image_size || null,
				viewport_css_size: s.viewportCssSize || s.viewport_css_size || null,
				canvas_size: s.canvasSize || s.canvas_size
					|| s.viewportCssSize || s.viewport_css_size
					|| s.imageSize || s.image_size || null,
				slide_bounds: s.slideBounds || s.slide_bounds || null,
				scale_bar: sc?.scale_bar || s.scale_bar || null,
				mpp_um_per_px: sc?.mpp_um_per_px ?? s.mpp_um_per_px ?? null,
				slide_mpp_um_per_px: sc?.slide_mpp_um_per_px ?? s.slide_mpp_um_per_px ?? null,
				finest_mpp_um_per_px: sc?.finest_mpp_um_per_px ?? s.finest_mpp_um_per_px ?? null,
				current_z: sc?.current_z ?? s.current_z ?? null,
				available_mpp_um_per_px: sc?.available_mpp_um_per_px || s.available_mpp_um_per_px || null,
				available_zooms: sc?.available_zooms || s.available_zooms || null,
			};
		});
		return {
			id: `local-user-${Date.now()}`,
			role: "user",
			content: question,
			created_at: new Date().toISOString(),
			source: "chat",
			optimistic: true,
			anno_required: Boolean(annoRequired),
			full_analysis: Boolean(fullAnalysis),
			attachments: attachments.length ? attachments : undefined,
		};
	};

	applyServerChat = (data, baseMessages) => {
		let next = data.chat?.messages || [
			...(baseMessages || []),
			...(data.messages || []),
		];
		if (data.ok === false && next.length) {
			const last = next[next.length - 1];
			if (last?.role === "assistant") {
				next = [...next.slice(0, -1), {...last, failed: true}];
			}
		}
		return this.assignSerialSiteLabels(next);
	};

	enqueueQuestion = ({question, sites, reuseSites, annoRequired, fullAnalysis, isAutoFollowUp}) => {
		if (!question || !this.props.slide) return Promise.resolve(false);
		if (!isAutoFollowUp) {
			this.setState({autoViewDepth: 0});
			clearPendingHandoff();
		}
		if (this.isCursorProvider() && this.state.cursor?.state !== "connected") {
			error_manager("Connect Cursor first", "Sign in with Cursor to use this provider.");
			return Promise.resolve(false);
		}

		const wantAnno = annoRequired != null ? Boolean(annoRequired) : Boolean(this.state.annoRequired);
		const wantFull = fullAnalysis != null ? Boolean(fullAnalysis) : this.state.fullAnalysis !== false;
		// Only send images the user captured into the tray (or retry reuse).
		// Do not auto-screenshot on every send — saves tokens/context.
		let siteList = reuseSites || sites || [];
		if (!Array.isArray(siteList)) siteList = [];

		const queueId = `q-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
		const optimistic = this.buildOptimisticUserMessage(question, siteList, wantAnno, wantFull);
		optimistic.queue_id = queueId;
		const waitingAssistant = {
			id: `local-wait-${queueId}`,
			role: "assistant",
			content: "",
			status: "waiting",
			queue_id: queueId,
			created_at: new Date().toISOString(),
			optimistic: true,
			anno_required: wantAnno,
		};
		const item = {
			queueId,
			question,
			annoRequired: wantAnno,
			fullAnalysis: wantFull,
			siteList: siteList.map((s) => ({
				label: s.label,
				blob: s.blob || null,
				fileId: s.fileId || null,
				previewUrl: s.previewUrl || null,
				urlSafe: s.urlSafe || null,
				mapState: s.mapState || s.map_state,
				extent: s.extent || null,
				imageSize: s.imageSize || s.image_size || null,
				viewportCssSize: s.viewportCssSize || s.viewport_css_size || null,
				canvasSize: s.canvasSize || s.canvas_size
					|| s.viewportCssSize || s.viewport_css_size
					|| s.imageSize || s.image_size || null,
				slideBounds: s.slideBounds || s.slide_bounds || null,
				scaleContext: s.scaleContext || s.scale_context || null,
				// Auto-captured FOVs: provenance for the server (parent recall / coverage).
				sourceSite: s.viewResolved?.sourceSite || s.sourceSite || null,
				viewIntent: s.viewIntent || s.viewRequest?.intent || null,
			})),
			provider: this.state.provider,
			modelId: this.state.modelId,
			userMsgId: optimistic.id,
			assistantMsgId: waitingAssistant.id,
		};
		this.lastSend = {question, sites: item.siteList, annoRequired: wantAnno, fullAnalysis: wantFull};
		this.persistProviderModel(item.provider, item.modelId);
		return new Promise((resolve) => {
			this.stickToBottom = true;
			this.setState((prev) => ({
				messages: [...(prev.messages || []).filter((m) => !m.local_error), optimistic, waitingAssistant],
				sendQueue: [...(prev.sendQueue || []), item],
				value: "",
				capturedImages: [],
				// Keep checkbox state for next message (user can toggle off).
			}), () => {
				this.scrollToBottom({force: true});
				this.drainSendQueue();
				resolve(true);
			});
		});
	};

	drainSendQueue = () => {
		if (this.queueBusy) return;
		const queue = this.state.sendQueue || [];
		if (!queue.length) {
			this.setState({loading: false, activeQueueId: null});
			this.stopThinkingRotation();
			return;
		}
		// Items stay in sendQueue until their POST returns; never re-send one already in flight.
		const item = queue.find((q) => !this.inFlightQueueIds.has(q.queueId));
		if (!item) return;
		this.inFlightQueueIds.add(item.queueId);
		this.queueBusy = true;
		this.setState({
			loading: true,
			activeQueueId: item.queueId,
			thinkingMessageIndex: 0,
			messages: (this.state.messages || []).map((m) => (
				m.queue_id === item.queueId && m.role === "assistant"
					? {...m, status: "pending"}
					: m
			)),
		}, () => this.startThinkingRotation());
		this.runQueuedItem(item);
	};

	runQueuedItem = async (item) => {
		const extension = this.props.extension || ScreenshotExtensions.JPEG;
		const baseMessages = (this.state.messages || []).filter(
			(m) => !m.optimistic && !m.local_error && m.queue_id !== item.queueId
		);
		try {
			const data = await sendChatMessageWithOptionalScreenshot({
				slide: this.props.slide,
				question: item.question,
				provider: item.provider,
				modelId: item.modelId,
				displaySetting: this.props.displaySetting,
				captureScreenshot: false,
				extension,
				sites: item.siteList,
				annoRequired: Boolean(item.annoRequired),
				fullAnalysis: item.fullAnalysis !== false,
				chatId: this.state.chatId,
			});
			if (data.chat?.chat_id && data.chat.chat_id !== this.state.chatId) {
				this.setState({
					chatId: data.chat.chat_id,
					chatTitle: data.chat.title || this.state.chatTitle,
				});
			}
			this.inFlightQueueIds.delete(item.queueId);
			if (data.job_id) {
				// Hold the queue until this Cursor job finishes (IDE-style).
				const next = this.applyServerChat(data, baseMessages);
				const assistantId = (data.messages || []).find((m) => m.job_id)?.id;
				this.setState((prev) => ({
					messages: next,
					sendQueue: (prev.sendQueue || []).filter((q) => q.queueId !== item.queueId),
					activeQueueId: item.queueId,
					loading: true,
				}), () => {
					this.prefetchAttachmentBlobs(next);
					this.scrollToBottomIfSticky();
					this.pollJobUntilDone(data.job_id, assistantId);
				});
				return;
			}
			const next = this.applyServerChat(data, baseMessages);
			this.setState((prev) => ({
				messages: next,
				sendQueue: (prev.sendQueue || []).filter((q) => q.queueId !== item.queueId),
			}), () => {
				this.prefetchAttachmentBlobs(next);
				this.scrollToBottomIfSticky();
				this.queueBusy = false;
				this.drainSendQueue();
			});
		} catch (err) {
			this.inFlightQueueIds.delete(item.queueId);
			const busy = err?.response?.status === 409 && err?.response?.data?.error_code === "chat_busy";
			if (busy) {
				// Another tab (or an earlier send) is still answering in this chat: drop the
				// duplicate, keep following the live job instead of starting a parallel one.
				const liveJob = err.response.data.job_id;
				this.setState((prev) => ({
					messages: (prev.messages || []).filter((m) => m.queue_id !== item.queueId),
					sendQueue: (prev.sendQueue || []).filter((q) => q.queueId !== item.queueId),
				}), () => {
					notifyInfo("Morpho Chat", "This chat is still answering a previous message; your duplicate was not sent.");
					if (liveJob) {
						// Follow the live job; applyJobTerminal's finish() releases the queue.
						this.queueBusy = true;
						this.setState({loading: true});
						this.pollJobUntilDone(liveJob, null);
						this.loadChat(this.state.chatId);
					} else {
						this.queueBusy = false;
						this.drainSendQueue();
					}
				});
				return;
			}
			const errMsg = {
				id: `local-err-${Date.now()}`,
				role: "assistant",
				content: `AI unavailable: ${err?.response?.data?.message || err?.response?.data || err?.message || "request failed"}`,
				failed: true,
				local_error: true,
				created_at: new Date().toISOString(),
			};
			this.setState((prev) => ({
				messages: [
					...(prev.messages || []).filter((m) => m.queue_id !== item.queueId || m.role === "user"),
					errMsg,
				].map((m) => (m.queue_id === item.queueId && m.role === "user" ? {...m, optimistic: false} : m)),
				sendQueue: (prev.sendQueue || []).filter((q) => q.queueId !== item.queueId),
			}), () => {
				this.scrollToBottomIfSticky();
				this.queueBusy = false;
				this.drainSendQueue();
			});
			error_manager("Morpho Chat failed", err);
		}
	};

	sendQuestion = (opts) => this.enqueueQuestion(opts);

	handleSubmit = async () => {
		const question = (this.state.value || "").trim();
		if (!question || !this.props.slide) return;
		let captures = this.state.capturedImages || [];
		const annoRequired = Boolean(this.state.annoRequired) || looksLikeAnnotateIntent(question);
		// Drawing needs pixels of what the user is looking at: attach the live view
		// when nothing was captured manually (the model cannot place geometry otherwise).
		if (!captures.length && this.props.slidemap && !this.isDashboard()
			&& (annoRequired || looksLikeViewportIntent(question))) {
			if (this.state.capturing) return;
			this.setState({capturing: true});
			try {
				const site = await this.captureCurrentSite(this.nextChatSiteLabel());
				if (site) captures = [site];
			} catch (err) {
				// Fall through and send text-only; the model will say it needs a view.
			}
			this.setState({capturing: false});
		}
		this.enqueueQuestion({
			question,
			sites: captures.length ? captures : null,
			annoRequired,
		});
	};

	handleRetry = (assistantMsg) => {
		if (!this.props.slide) return;
		const msgs = this.state.messages || [];
		const idx = msgs.findIndex((m) => m.id === assistantMsg.id);
		let question = this.lastSend?.question;
		let reuseSites = this.lastSend?.sites;
		let annoRequired = this.lastSend?.annoRequired;
		let fullAnalysis = this.lastSend?.fullAnalysis;
		if (idx > 0) {
			for (let i = idx - 1; i >= 0; i -= 1) {
				if (msgs[i].role === "user") {
					question = msgs[i].content;
					if (msgs[i].anno_required != null) annoRequired = Boolean(msgs[i].anno_required);
					if (msgs[i].full_analysis != null) fullAnalysis = Boolean(msgs[i].full_analysis);
					break;
				}
			}
		}
		if (assistantMsg?.anno_required != null) annoRequired = Boolean(assistantMsg.anno_required);
		if (!question) return;

		const trimmed = msgs.filter((m) => m.id !== assistantMsg.id && !m.local_error);
		this.setState({messages: trimmed}, () => {
			const canReuse = reuseSites?.length && this.lastSend?.question === question;
			this.sendQuestion({
				question,
				sites: null,
				reuseSites: canReuse ? reuseSites : null,
				annoRequired: Boolean(annoRequired),
				fullAnalysis,
			});
		});
	};

	findAttachmentByLabel = (label) => {
		const L = (label || "").toUpperCase();
		if (!L) return null;
		for (let i = 0; i < (this.state.messages || []).length; i++) {
			const m = this.state.messages[i];
			if (m.role !== "user") continue;
			const found = (m.attachments || []).find((a) => (a.label || "").toUpperCase() === L);
			if (found) return found;
		}
		return (this.state.capturedImages || []).find((s) => (s.label || "").toUpperCase() === L) || null;
	};

	onSiteRefClick = (label, _msgAttachments, {showImage = false} = {}) => {
		const att = this.findAttachmentByLabel(label);
		if (!att) return;
		this.onAttachmentClick(att, {showImage});
	};

	onAttachmentClick = (att, {showImage = false} = {}) => {
		if (!showImage && att?.map_state && this.isOtherPart(att)) {
			this.navigateToPart(this.attSlideId(att), {mapState: att.map_state});
			return;
		}
		if (att?.map_state && this.props.slidemap && !this.isOtherPart(att)) {
			animateToState(this.props.slidemap.getView(), att.map_state);
		} else if (att?.map_state && this.props.slide?.id && (this.isDashboard() || !this.props.slidemap)) {
			const url = gammaUrlForMapState(this.props.slide.id, att.map_state, {openChat: true});
			if (url) window.open(url, "_blank", "noopener");
		}
		if (showImage) {
			const key = this.attachmentBlobKey(att);
			const src = this.attachmentSrc(att) || (key && this.state.blobSrcByKey[key]) || null;
			if (src) this.setState({lightbox: {src, label: att.label || "A"}});
		}
	};

	openSlideInViewer = ({mapState = null, openChat = true} = {}) => {
		const slideId = this.props.slide?.id;
		if (!slideId) return;
		const url = gammaUrlForMapState(slideId, mapState || this.currentMapState(), {openChat});
		if (url) window.open(url, "_blank", "noopener");
	};

	mediaUrl = (urlSafe) => {
		if (!urlSafe) return null;
		if (/^https?:\/\//i.test(urlSafe) || urlSafe.startsWith("blob:")) return urlSafe;
		return window.location.origin + getStateAwareUrl("") + urlSafe;
	};

	attachmentSrc = (att) => {
		if (att?.preview_url) return att.preview_url;
		if (att?.url_safe) return this.mediaUrl(att.url_safe);
		if (att?.file_id) {
			const files = this.props.caseFiles || [];
			const f = files.find((x) => String(x.id) === String(att.file_id));
			if (f?.url_safe) return this.mediaUrl(f.url_safe);
		}
		return null;
	};

	attachmentBlobKey = (att) => {
		const slideId = this.attSlideId(att);
		if (!slideId || !att?.rel_path) return null;
		return `${slideId}:${att.rel_path}`;
	};

	/** Screenshot URL for an attachment, fetching the protected blob if needed. */
	resolveAttachmentSrcAsync = async (att) => {
		const key = this.attachmentBlobKey(att);
		const known = this.attachmentSrc(att)
			|| (key && (this.state.blobSrcByKey[key] || (typeof this.blobUrls[key] === "string" && this.blobUrls[key])));
		if (known || !key) return known || null;
		const url = getStateAwareUrl(
			`/api/slide_ai/attachment/?slide_id=${encodeURIComponent(this.attSlideId(att))}&rel_path=${encodeURIComponent(att.rel_path)}`
		);
		const res = await axios.get(url, {...AxiosConfig(), responseType: "blob"});
		const obj = URL.createObjectURL(res.data);
		this.blobUrls[key] = obj;
		this.setState((prev) => ({blobSrcByKey: {...prev.blobSrcByKey, [key]: obj}}));
		return obj;
	};

	ensureBlobSrc = (att) => {
		const key = this.attachmentBlobKey(att);
		if (!key || this.state.blobSrcByKey[key] || this.blobUrls[key]) return;
		const slideId = this.attSlideId(att);
		const url = getStateAwareUrl(
			`/api/slide_ai/attachment/?slide_id=${encodeURIComponent(slideId)}&rel_path=${encodeURIComponent(att.rel_path)}`
		);
		this.blobUrls[key] = true;
		axios.get(url, {...AxiosConfig(), responseType: "blob"})
			.then((res) => {
				const obj = URL.createObjectURL(res.data);
				this.blobUrls[key] = obj;
				this.setState((prev) => ({
					blobSrcByKey: {...prev.blobSrcByKey, [key]: obj},
				}));
			})
			.catch(() => {
				delete this.blobUrls[key];
			});
	};

	renderAttachment = (att, i, msgAttachments) => {
		const key = this.attachmentBlobKey(att);
		const src = this.attachmentSrc(att) || (key && this.state.blobSrcByKey[key]) || null;
		const label = att.label || siteLabelAt(i);
		return (
			<Box key={i} sx={{mt: 0.5, display: "inline-block", mr: 1, verticalAlign: "top"}}>
				<Typography variant="caption" sx={{color: CHAT_TEXT_COLOR, opacity: 0.85, display: "block"}}>
					Image {label}
					{this.isCaseChat() && this.partForSlide(this.attSlideId(att))
						? ` · ${this.partForSlide(this.attSlideId(att))}` : ""}
					{magLabelForZ(att.current_z, att.available_zooms) ? ` · ${magLabelForZ(att.current_z, att.available_zooms)}` : ""}
					{att.source_site ? ` · from ${att.source_site}` : ""}
				</Typography>
				{src ? (
					<img
						src={src}
						alt={`Image ${label}`}
						title={`Go to Image ${label} (double-click to enlarge)`}
						style={{maxWidth: 140, maxHeight: 100, cursor: "pointer", borderRadius: 4, border: "1px solid rgba(255,255,255,0.25)"}}
						onClick={() => this.onAttachmentClick(att)}
						onDoubleClick={() => this.onAttachmentClick(att, {showImage: true})}
					/>
				) : att?.map_state ? (
					<Button size="small" color="secondary" onClick={() => this.onAttachmentClick(att)}>
						Go to {label}
					</Button>
				) : (
					<Typography variant="caption" sx={{opacity: 0.6}}>Loading image…</Typography>
				)}
				{src && (
					<Stack direction="row" spacing={0.5} sx={{mt: 0.25}}>
						<Button size="small" color="secondary" sx={{minWidth: 0, px: 0.5, fontSize: "0.7rem"}}
							onClick={() => this.onAttachmentClick(att)}>
							Go
						</Button>
						<Button size="small" color="secondary" sx={{minWidth: 0, px: 0.5, fontSize: "0.7rem"}}
							onClick={() => this.onAttachmentClick(att, {showImage: true})}>
							Show
						</Button>
					</Stack>
				)}
			</Box>
		);
	};

	formatThoughtFor = (thoughtMs) => {
		const ms = Number(thoughtMs);
		if (!Number.isFinite(ms) || ms < 0) return null;
		if (ms < 1000) return `Thought for ${Math.max(1, Math.round(ms))} ms`;
		const sec = ms / 1000;
		const label = sec < 10 ? sec.toFixed(1) : String(Math.round(sec));
		return `Thought for ${label}s`;
	};

	formatUsageFor = (usage, model, timings) => {
		if (!usage || typeof usage !== "object") return null;
		const tin = Number(usage.tokens_in);
		const tout = Number(usage.tokens_out);
		const parts = [];
		if (Number.isFinite(tin)) parts.push(`${formatTokenCount(tin)} in`);
		if (Number.isFinite(tout)) parts.push(`${formatTokenCount(tout)} out`);
		if (!parts.length && !(timings && typeof timings === "object")) return null;
		const cache = Number(usage.tokens_cache_read) || 0;
		if (cache > 0) parts.push(`${formatTokenCount(cache)} cache`);
		const window = estimateContextWindow(model);
		if (Number.isFinite(tin) && window > 0) {
			const pct = Math.min(100, Math.round((tin / window) * 100));
			parts.push(`~${pct}% ctx`);
		}
		const rss = Number(usage.rss_mb_peak_tree);
		if (Number.isFinite(rss) && rss > 0) parts.push(`RAM ${Math.round(rss)}MB`);
		const cpu = Number(usage.cpu_pct_avg);
		if (Number.isFinite(cpu) && cpu > 0) parts.push(`CPU ~${Math.round(cpu)}%`);
		if (timings && typeof timings === "object") {
			const fmt = (ms) => {
				const n = Number(ms);
				if (!Number.isFinite(n) || n < 0) return null;
				if (n < 1000) return `${Math.round(n)}ms`;
				return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}s`;
			};
			const segs = [];
			const q = fmt(timings.queue_wait_ms);
			const cli = fmt(timings.cli_wall_ms);
			const e2e = fmt(timings.e2e_server_ms);
			if (q) segs.push(`q ${q}`);
			if (cli) segs.push(`cli ${cli}`);
			if (e2e) segs.push(`e2e ${e2e}`);
			if (segs.length) parts.push(segs.join(" · "));
		}
		return parts.length ? parts.join(" · ") : null;
	};

	chatUsageTotals = () => {
		let tin = 0;
		let tout = 0;
		let cacheRead = 0;
		let cacheWrite = 0;
		let n = 0;
		(this.state.messages || []).forEach((m) => {
			if (m?.role !== "assistant" || !m.usage) return;
			const a = Number(m.usage.tokens_in);
			const b = Number(m.usage.tokens_out);
			if (Number.isFinite(a)) { tin += a; n += 1; }
			if (Number.isFinite(b)) { tout += b; }
			cacheRead += Number(m.usage.tokens_cache_read) || 0;
			cacheWrite += Number(m.usage.tokens_cache_write) || 0;
		});
		if (!n) return null;
		return {
			tokens_in: tin,
			tokens_out: tout,
			tokens_cache_read: cacheRead,
			tokens_cache_write: cacheWrite,
			messages: n,
		};
	};

	usageStatsText = (msg) => {
		const u = msg?.usage || {};
		const t = msg?.timings || {};
		const lines = [`model: ${msg?.model || "unknown"}`];
		if (msg?.created_at) lines.push(`at: ${msg.created_at}`);
		const tok = [
			["in", u.tokens_in], ["out", u.tokens_out],
			["cache read", u.tokens_cache_read], ["cache write", u.tokens_cache_write],
		].filter(([, v]) => Number.isFinite(Number(v)) && v !== null && v !== undefined);
		if (tok.length) lines.push(`tokens: ${tok.map(([k, v]) => `${k} ${v}`).join(" · ")}`);
		const window = estimateContextWindow(msg?.model);
		if (Number.isFinite(Number(u.tokens_in)) && window > 0) {
			lines.push(`context: ~${Math.min(100, Math.round((Number(u.tokens_in) / window) * 100))}% of ${window}`);
		}
		const res = [
			["RAM peak MB", u.rss_mb_peak_tree], ["CPU avg %", u.cpu_pct_avg], ["CPU s", u.cpu_sec_total],
		].filter(([, v]) => Number(v) > 0).map(([k, v]) => `${k} ${Math.round(Number(v))}`);
		if (res.length) lines.push(`resources: ${res.join(" · ")}`);
		if (msg?.thought_ms != null) lines.push(`thought_ms: ${msg.thought_ms}`);
		const tm = Object.entries(t).filter(([k]) => k.endsWith("_ms")).map(([k, v]) => `${k} ${v}`);
		if (tm.length) lines.push(`timings: ${tm.join(" · ")}`);
		return lines.join("\n");
	};

	chatStatsText = () => {
		const cols = ["#", "at", "model", "in", "out", "cache_read", "cache_write", "thought_ms", "cli_wall_ms", "e2e_server_ms"];
		const rows = [cols.join("\t")];
		let i = 0;
		(this.state.messages || []).forEach((m) => {
			if (m?.role !== "assistant" || !(m.usage || m.timings || m.thought_ms != null)) return;
			i += 1;
			const u = m.usage || {};
			const t = m.timings || {};
			rows.push([
				i, m.created_at || "", m.model || "", u.tokens_in ?? "", u.tokens_out ?? "",
				u.tokens_cache_read ?? "", u.tokens_cache_write ?? "", m.thought_ms ?? "",
				t.cli_wall_ms ?? "", t.e2e_server_ms ?? "",
			].join("\t"));
		});
		const tot = this.chatUsageTotals() || {};
		rows.push([
			"total", "", `${tot.messages || 0} replies`, tot.tokens_in || 0, tot.tokens_out || 0,
			tot.tokens_cache_read || 0, tot.tokens_cache_write || 0, "", "", "",
		].join("\t"));
		return [`Morpho Chat: ${this.state.chatTitle || this.state.chatId || ""}`, ...rows].join("\n");
	};

	copyText = async (text, what) => {
		let ok = false;
		try {
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(text);
				ok = true;
			}
		} catch (e) { /* fall back below */ }
		if (!ok) {
			const ta = document.createElement("textarea");
			ta.value = text;
			ta.setAttribute("readonly", "");
			ta.style.position = "fixed";
			ta.style.opacity = "0";
			document.body.appendChild(ta);
			ta.select();
			try { ok = document.execCommand("copy"); } catch (e) { ok = false; }
			document.body.removeChild(ta);
		}
		notifyInfo("Morpho Chat", ok ? `${what} copied` : `Could not copy ${what.toLowerCase()}`);
	};

	/**
	 * Images a reply can annotate: the nearest earlier turn's images first (default
	 * site), then every other image in the chat so source_site can name any of them.
	 */
	attachmentsForAiSave = (msg) => {
		const messages = this.state.messages || [];
		const idx = messages.findIndex((m) => m.id === msg.id);
		const end = idx >= 0 ? idx : messages.length;
		const out = [];
		const seen = new Set();
		const add = (a) => {
			const key = String(a?.label || "").toUpperCase() || a?.rel_path || a?.file_id;
			if (!a || seen.has(key)) return;
			seen.add(key);
			out.push(a);
		};
		for (let i = end - 1; i >= 0; i--) {
			if (messages[i].role === "user" && (messages[i].attachments || []).length) {
				messages[i].attachments.forEach(add);
				break;
			}
		}
		for (let i = end - 1; i >= 0; i--) (messages[i].attachments || []).forEach(add);
		messages.forEach((m) => (m.attachments || []).forEach(add));
		return out;
	};

	/**
	 * Place AI geo features via CaptureFrame (rotation-aware canvas→slide).
	 * @param {object} msg
	 * @param {object[]|null} onlyItems - subset from listAiGeoFeatures; null = all
	 * @param {{restoreView?: boolean}} opts
	 */
	placeAiGeoFeatures = async (msg, onlyItems = null, opts = {}) => {
		if (!this.props.slide || !this.props.slidemap || this.state.savingGeoForMsg) return;
		if (this.isDashboard()) {
			error_manager("Open the slide in the viewer to place annotations", null);
			return;
		}
		const all = listAiGeoFeatures(msg.content);
		let items = onlyItems?.length ? onlyItems : all;
		// Skip FOV-filling slabs on bulk draw; allow explicit single-feature draw with a warn.
		if (!onlyItems?.length) {
			items = items.filter((it) => !it.suspectFovFill);
		}
		if (!items.length) {
			error_manager(
				all.length
					? "Every region in this reply covers almost the whole image; draw one individually if you still want it"
					: "This reply has no regions to mark",
				null,
			);
			return;
		}

		const attachments = this.attachmentsForAiSave(msg)
			.filter((a) => !this.isCaseChat() || !this.isOtherPart(a));
		const restoreView = opts.restoreView !== false && !onlyItems;
		this.setState({
			savingGeoForMsg: msg.id,
			placingGeoProgress: {msgId: msg.id, site: null, phase: "start"},
		});
		try {
			const {saved, placedKeys, failures = []} = await placeAiFeaturesOnSlide({
				slidemap: this.props.slidemap,
				slide: this.props.slide,
				featureItems: items,
				attachments,
				slideId: this.props.slide.id,
				creatorId: getLoggedInUserId(),
				annoDrawer: this.props.activeAnnoDrawer,
				defaultZ: this.props.z,
				uperpixel: this.props.slide.uperpixel || 1,
				restoreView,
				resolveAttachmentSrc: this.resolveAttachmentSrcAsync,
				onProgress: ({site, phase}) => {
					this.setState({placingGeoProgress: {msgId: msg.id, site, phase}});
				},
				addPayload: (payload) => {
					this.props.dispatch(addAnnotation(this.props.activeMapId, payload));
				},
			});
			if (placedKeys?.length) {
				this.setState((prev) => {
					const prevKeys = prev.drawnAiFeatureKeys[msg.id] || [];
					const merged = Array.from(new Set([...prevKeys, ...placedKeys]));
					return {
						drawnAiFeatureKeys: {...prev.drawnAiFeatureKeys, [msg.id]: merged},
					};
				});
			}
			if (failures.length) {
				const why = {
					unknown_image: (f) => {
						const other = this.findAttachmentByLabel(f.site);
						const part = other && this.isOtherPart(other) && this.partForSlide(this.attSlideId(other));
						return part
							? `Image ${f.site} is on ${part} — open that part to draw it`
							: `Image ${f.site} is not in this chat`;
					},
					no_image_size: (f) => `Image ${f.site} has no size information`,
					no_position: (f) => `Image ${f.site} has no saved slide position`,
					bad_geometry: () => "the shape could not be read",
				};
				const lines = Array.from(new Set(failures.map((f) => (why[f.reason] || (() => f.reason))(f))));
				const msgText = `${saved ? `Placed ${saved}; ` : ""}${failures.length} region${failures.length > 1 ? "s" : ""} not placed: ${lines.join("; ")}`;
				if (saved) notifyInfo("Morpho Chat", msgText);
				else error_manager(msgText, null);
			} else if (!saved) {
				error_manager("No valid AI regions to place", null);
			}
		} catch (err) {
			error_manager("Failed to place AI annotations", err);
		} finally {
			this.setState({savingGeoForMsg: null, placingGeoProgress: null});
		}
	};

	handleSaveGeoJson = (msg) => {
		this.placeAiGeoFeatures(msg, null, {restoreView: true});
	};

	handleDrawOneAiFeature = (msg, item) => {
		// Single feature: stay on that site after draw (no restore).
		this.placeAiGeoFeatures(msg, [item], {restoreView: false});
	};

	/**
	 * Navigate to a feature: center on its region; for closer_look also raise magnification.
	 */
	goToAiFeature = async (msg, item) => {
		if (!this.props.slidemap || this.isDashboard()) {
			error_manager("Open the slide in the viewer to navigate", null);
			return;
		}
		const attachments = this.attachmentsForAiSave(msg);
		const ms = mapStateForAiFeature(item, attachments, {
			slidemap: this.props.slidemap,
			slide: this.props.slide,
			currentMapState: this.currentMapState(),
		});
		if (!ms) {
			if (item.sourceSite) this.onSiteRefClick(item.sourceSite, msg.attachments);
			return;
		}
		await navigateToMapStateVerified(this.props.slidemap, ms, {timeoutMs: 6000});
	};

	toggleLegendExpanded = (msgId) => {
		this.setState((prev) => ({
			legendExpandedByMsg: {
				...prev.legendExpandedByMsg,
				[msgId]: !prev.legendExpandedByMsg[msgId],
			},
		}));
	};

	renderFeatureChip = (msg, item, {compact = false, drawn = false} = {}) => {
		const busy = Boolean(this.state.savingGeoForMsg);
		const dash = this.isDashboard();
		const swatch = item.suspectFovFill ? "#FFC107" : (item.color || "#E91E63");
		const muted = item.priority === "secondary";
		const goTitle = item.classKey === "closer_look" || item.classKey === "roi"
			? "Go to region at higher magnification"
			: "Go to on slide";
		const drawTitle = item.suspectFovFill
			? "Likely fills the whole FOV — draw may look wrong"
			: (drawn ? "Already drawn — draw again" : "Draw this feature on the slide");
		return (
			<Box
				key={item.key}
				component="span"
				sx={{
					display: "inline-flex",
					alignItems: "center",
					gap: 0.25,
					mx: 0.2,
					my: 0.15,
					pl: 0.6,
					pr: 0.25,
					py: 0.1,
					borderRadius: 1,
					border: `1px solid ${swatch}99`,
					backgroundColor: muted ? "rgba(255,255,255,0.04)" : `${swatch}22`,
					verticalAlign: "middle",
					maxWidth: "100%",
					opacity: item.suspectFovFill ? 0.85 : 1,
				}}
			>
				<span
					style={{
						width: 8,
						height: 8,
						borderRadius: 2,
						background: swatch,
						flexShrink: 0,
					}}
				/>
				<Typography
					component="span"
					sx={{
						fontSize: compact ? "0.72rem" : "0.78rem",
						color: "#fff",
						fontWeight: muted ? 400 : 600,
						whiteSpace: "nowrap",
					}}
				>
					{item.label}
					{item.sourceSite ? (
						<Box component="span" sx={{opacity: 0.65, fontWeight: 400}}> · {item.sourceSite}</Box>
					) : null}
					{item.suspectFovFill ? (
						<Box component="span" sx={{opacity: 0.75, fontWeight: 400, ml: 0.35}}>~FOV</Box>
					) : null}
				</Typography>
				<Tooltip title={goTitle}>
					<span>
						<IconButton
							size="small"
							disabled={dash || busy}
							onClick={(e) => {
								e.preventDefault();
								this.goToAiFeature(msg, item);
							}}
							sx={{color: swatch, p: 0.2}}
						>
							<PlaceOutlinedIcon sx={{fontSize: 14}} />
						</IconButton>
					</span>
				</Tooltip>
				<Tooltip title={drawTitle}>
					<span>
						<Button
							size="small"
							disabled={dash || busy}
							onClick={(e) => {
								e.preventDefault();
								this.handleDrawOneAiFeature(msg, item);
							}}
							sx={{
								minWidth: 0,
								px: 0.5,
								py: 0,
								fontSize: "0.65rem",
								textTransform: "none",
								color: "#fff",
								opacity: drawn ? 0.75 : 1,
							}}
						>
							{drawn ? "✓" : "Draw"}
						</Button>
					</span>
				</Tooltip>
			</Box>
		);
	};

	/** Label of the most recent image at or before `msg` (context for bare R1 / [x,y] refs). */
	defaultImageLabelFor = (msg) => {
		const msgs = this.state.messages || [];
		let idx = msg ? msgs.findIndex((m) => m.id === msg.id) : msgs.length - 1;
		if (idx < 0) idx = msgs.length - 1;
		for (let i = idx; i >= 0; i -= 1) {
			const atts = msgs[i]?.attachments || [];
			if (atts.length) return String(atts[atts.length - 1].label || "").toUpperCase() || null;
		}
		return null;
	};

	/** Has any later, finer capture on the same slide looked inside this region of `ov`? */
	regionExamined = (ov, region, allAtts) => {
		const bb = region?.bbox;
		const extO = ov?.extent;
		const canvas = ov?.canvas_size || ov?.viewport_css_size || ov?.image_size;
		if (!bb || !extO || !canvas) return null;
		const sx = canvas[0] / (extO[2] - extO[0]);
		const sy = canvas[1] / (extO[3] - extO[1]);
		const finer = (allAtts || []).filter((a) => a !== ov
			&& String(this.attSlideId(a)) === String(this.attSlideId(ov))
			&& a.extent && (a.mpp_um_per_px || Infinity) < (ov.mpp_um_per_px || 0));
		return finer.some((a) => {
			const e = a.extent;
			const cx = ((e[0] + e[2]) / 2 - extO[0]) * sx;
			const cy = (extO[3] - (e[1] + e[3]) / 2) * sy;
			return cx >= bb[0] && cx <= bb[2] && cy >= bb[1] && cy <= bb[3];
		});
	};

	/** Go to a canvas point / box on image `label` (hands off when that image is on another part). */
	goToCanvasTarget = (msg, label, {point = null, bbox = null}) => {
		const req = {
			id: `goto:${label}`,
			sourceSite: label,
			point: point || (bbox ? [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2] : null),
			bbox,
			fitBbox: bbox ? {
				minX: bbox[0], minY: bbox[1], maxX: bbox[2], maxY: bbox[3],
				width: bbox[2] - bbox[0], height: bbox[3] - bbox[1],
			} : null,
			intent: bbox ? "higher_res" : "recenter",
			reason: "",
		};
		return this.goToViewRequest(req, msg?.attachments, msg?.id);
	};

	/**
	 * Inline chip for a region (R1/B2), a point or a box the model mentioned: the whole
	 * badge is Go to; Draw places it as an annotation.
	 */
	renderTargetChip = (msg, {text, label, point = null, bbox = null, contour = null, kind = "region", examined = null, title = null}) => {
		const busy = Boolean(this.state.savingGeoForMsg);
		const dash = this.isDashboard();
		const att = this.findAttachmentByLabel(label);
		const otherPart = att && this.isOtherPart(att);
		const color = kind === "basophilic" ? "#CE93D8" : (kind === "tissue" ? "#80DEEA" : "#9ecbff");
		const item = msg ? syntheticGeoItem({label: text, sourceSite: label, kind, point, bbox, contour, color}) : null;
		const goTitle = title || (otherPart
			? `Open ${this.partForSlide(this.attSlideId(att)) || "that part"} at this spot`
			: `Go to ${text} on Image ${label}`);
		return (
			<Box
				component="span"
				sx={{
					display: "inline-flex", alignItems: "center", gap: 0.15, mx: 0.2, my: 0.1,
					pl: 0.5, pr: 0.15, py: 0.05, borderRadius: 1, verticalAlign: "middle",
					border: `1px solid ${color}${examined === false ? "66" : "aa"}`,
					borderStyle: examined === false ? "dashed" : "solid",
					backgroundColor: `${color}1a`,
				}}
			>
				<Tooltip title={examined === false ? `${goTitle} — not examined yet` : goTitle}>
					<Box
						component="button"
						type="button"
						disabled={dash}
						onClick={(e) => { e.preventDefault(); this.goToCanvasTarget(msg, label, {point, bbox}); }}
						sx={{
							all: "unset", cursor: dash ? "default" : "pointer", fontSize: "0.72rem",
							color, fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1.4,
							"&:hover": {textDecoration: "underline"},
						}}
					>
						{text}
						{examined === false ? <Box component="span" sx={{opacity: 0.7, fontWeight: 400}}> · new</Box> : null}
					</Box>
				</Tooltip>
				{item && !otherPart ? (
					<Tooltip title="Draw on the slide">
						<span>
							<Button
								size="small"
								disabled={dash || busy}
								onClick={(e) => { e.preventDefault(); this.handleDrawOneAiFeature(msg, item); }}
								sx={{minWidth: 0, px: 0.4, py: 0, fontSize: "0.62rem", textTransform: "none", color: "#fff", opacity: 0.85}}
							>
								Draw
							</Button>
						</span>
					</Tooltip>
				) : null}
			</Box>
		);
	};

	/** Detected tissue fragments / basophilic regions of this message's images as chips. */
	renderRegionChips = (msg) => {
		const atts = (msg?.attachments || []).filter((a) => attachmentRegions(a).length);
		if (!atts.length) return null;
		const all = this.collectChatAttachments(this.state.messages);
		return atts.map((att) => {
			const label = String(att.label || "").toUpperCase();
			const regions = attachmentRegions(att);
			const tissue = regions.filter((r) => r.kind === "tissue");
			// Coverage marks only on overviews: the coarsest multi-fragment image of this slide,
			// or one that already has finer captures inside it.
			const sameSlide = all.filter((a) => String(this.attSlideId(a)) === String(this.attSlideId(att)));
			const coarsestMulti = sameSlide
				.filter((a) => attachmentRegions(a).filter((r) => r.kind === "tissue").length > 1)
				.sort((a, b) => (b.mpp_um_per_px || 0) - (a.mpp_um_per_px || 0))[0];
			const hasFiner = sameSlide.some((a) => a !== att && (a.mpp_um_per_px || Infinity) < (att.mpp_um_per_px || 0));
			const multi = tissue.length > 1 && (coarsestMulti === att || hasFiner);
			return (
				<Box key={`regions:${label}`} sx={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.25, mt: 0.35}}>
					<Typography variant="caption" sx={{color: CHAT_TEXT_COLOR, opacity: 0.65, mr: 0.25, fontSize: "0.68rem"}}>
						Image {label} regions
					</Typography>
					{regions.map((r) => this.renderTargetChip(msg, {
						text: r.id,
						label,
						bbox: r.bbox,
						contour: r.contour,
						kind: r.kind,
						examined: multi && r.kind === "tissue" ? this.regionExamined(att, r, all) : null,
						title: r.kind === "tissue"
							? `Tissue fragment ${r.id} — go to it on the slide`
							: `Cell-dense region ${r.id}${r.inRegion ? ` in ${r.inRegion}` : ""} — go to it`,
					}))}
				</Box>
			);
		});
	};

	renderMarkdown = (content, msgAttachments, msg = null, geoItems = []) => {
		const stripped = stripAiMachineBlocks(content || "");
		const withFeats = linkifyFeatureRefs(stripped, geoItems);
		// A reply can only refer to images that existed when it was written: scope region /
		// coordinate ownership to attachments up to this message so later images (with
		// their own R1, R2…) don't make earlier references ambiguous.
		const msgs = this.state.messages || [];
		const upto = msg ? msgs.findIndex((m) => m === msg || (m.id && m.id === msg.id)) : -1;
		const chatAtts = this.collectChatAttachments(upto >= 0 ? msgs.slice(0, upto + 1) : msgs);
		const defaultLabel = this.defaultImageLabelFor(msg);
		const withRegions = linkifyRegionRefs(withFeats, chatAtts, defaultLabel);
		const withCoords = linkifyCoordRefs(withRegions, chatAtts, defaultLabel);
		const linked = linkifySiteRefs(withCoords);
		const regionByImage = new Map(chatAtts.map((a) => [String(a.label || "").toUpperCase(), attachmentRegions(a)]));
		const byIndex = new Map(geoItems.map((it) => [it.index, it]));
		const drawnKeys = msg ? (this.state.drawnAiFeatureKeys[msg.id] || []) : [];
		return (
			<Box sx={{
				...markdownSx,
				textAlign: "left",
				display: "block",
				maxWidth: "100%",
				lineHeight: 1.55,
				"& p": {m: "0.5em 0"},
				"& h1, & h2, & h3": {mt: "0.85em", mb: "0.35em", letterSpacing: "0.01em"},
			}}>
				<ReactMarkdown
					remarkPlugins={[remarkGfm]}
					skipHtml
					components={{
						a: ({href, children}) => {
							const feat = href && /^#feat:(\d+)$/i.exec(href);
							if (feat && msg) {
								const item = byIndex.get(Number(feat[1]));
								if (item) {
									return this.renderFeatureChip(msg, item, {
										compact: true,
										drawn: drawnKeys.includes(item.key),
									});
								}
							}
							const reg = href && /^#region:([A-Za-z]{1,3}):([RB]\d{1,2})$/i.exec(href);
							if (reg) {
								const label = reg[1].toUpperCase();
								const id = reg[2].toUpperCase();
								const r = (regionByImage.get(label) || []).find((x) => x.id === id);
								if (r) {
									return this.renderTargetChip(msg, {
										text: id, label, bbox: r.bbox, contour: r.contour, kind: r.kind,
									});
								}
							}
							const pt = href && /^#coord:([A-Za-z]{1,3}):([\d.]+):([\d.]+)$/i.exec(href);
							if (pt) {
								return this.renderTargetChip(msg, {
									text: children, label: pt[1].toUpperCase(),
									point: [Number(pt[2]), Number(pt[3])], kind: "point",
								});
							}
							const bx = href && /^#box:([A-Za-z]{1,3}):([\d.]+):([\d.]+):([\d.]+):([\d.]+)$/i.exec(href);
							if (bx) {
								return this.renderTargetChip(msg, {
									text: children, label: bx[1].toUpperCase(),
									bbox: [Number(bx[2]), Number(bx[3]), Number(bx[4]), Number(bx[5])], kind: "box",
								});
							}
							const m = href && /^#site:([A-Za-z]+)$/i.exec(href);
							if (m) {
								const label = m[1].toUpperCase();
								return (
									<Box component="span" sx={{display: "inline-flex", gap: 0.5, alignItems: "center", mx: 0.25}}>
										<Button
											size="small"
											variant="outlined"
											color="secondary"
											onClick={(e) => {
												e.preventDefault();
												this.onSiteRefClick(label, msgAttachments);
											}}
											sx={{
												minWidth: 0,
												px: 0.75,
												py: 0,
												fontSize: "0.75rem",
												color: "#9ecbff",
												borderColor: "rgba(158,203,255,0.5)",
												textTransform: "none",
											}}
										>
											{children}
										</Button>
										<Tooltip title="Show image">
											<IconButton
												size="small"
												onClick={(e) => {
													e.preventDefault();
													this.onSiteRefClick(label, msgAttachments, {showImage: true});
												}}
												sx={{color: "#9ecbff", p: 0.25}}
											>
												<TravelExploreIcon sx={{fontSize: 14}} />
											</IconButton>
										</Tooltip>
									</Box>
								);
							}
							return (
								<a href={href} target="_blank" rel="noopener noreferrer">
									{children}
								</a>
							);
						},
						code: ({className, children, ...props}) => {
							const lang = (className || "").replace("language-", "");
							if (lang === "ai-geojson" || lang === "ai-view-request") return null;
							return <code className={className} {...props}>{children}</code>;
						},
					}}
				>
					{linked}
				</ReactMarkdown>
			</Box>
		);
	};

	renderMessage = (msg) => {
		const isUser = msg.role === "user";
		const isWaiting = !isUser && msg.status === "waiting";
		const isPending = !isUser && (msg.status === "pending" || (this.state.loading && msg.optimistic && msg.status !== "waiting"));
		const thoughtLabel = !isUser && !isWaiting && !isPending ? this.formatThoughtFor(msg.thought_ms) : null;
		const usageLabel = !isUser && !isWaiting
			? this.formatUsageFor(msg.usage, msg.model, isPending ? null : msg.timings)
			: null;
		const showRetry = !isUser && isAiUnavailableMessage(msg);
		const geoItems = !isUser && msg.content ? listAiGeoFeatures(msg.content) : [];
		const viewReqs = !isUser && msg.content ? listAiViewRequests(msg.content) : [];
		const geoFeatureCount = geoItems.length;
		const {primary: primaryFeats, secondary: secondaryFeats} = groupAiGeoFeaturesByPriority(geoItems);
		const drawnKeys = this.state.drawnAiFeatureKeys[msg.id] || [];
		// Textbook default: legend open; user can collapse.
		const legendOpen = this.state.legendExpandedByMsg[msg.id] !== false;
		const placing = this.state.placingGeoProgress?.msgId === msg.id
			? this.state.placingGeoProgress
			: null;
		return (
			<Box
				key={msg.id}
				sx={{
					mb: 1.5,
					textAlign: isUser ? "right" : "left",
					...(!isUser ? {
						px: 1.25,
						py: 1,
						borderRadius: 1.5,
						backgroundColor: "rgba(255,255,255,0.07)",
						border: "1px solid rgba(255,255,255,0.12)",
						boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
					} : {}),
				}}
			>
				<Typography variant="caption" sx={{opacity: 0.7, color: CHAT_TEXT_COLOR, display: "block"}}>
					{isUser ? "You" : "AI"}
					{msg.model ? ` · ${msg.model}` : ""}
					{msg.anno_required ? " · annotations requested" : ""}
					{isUser && msg.full_analysis === false ? " · full analysis off" : ""}
				</Typography>
				{isWaiting && (
					<Typography variant="body2" sx={{color: CHAT_TEXT_COLOR, opacity: 0.7, fontStyle: "italic"}}>
						Waiting…
					</Typography>
				)}
				{isPending && !isWaiting && (
					<Typography variant="body2" sx={{color: CHAT_TEXT_COLOR, opacity: 0.85, fontStyle: "italic"}}>
						{THINKING_MESSAGES[this.state.thinkingMessageIndex % THINKING_MESSAGES.length]}
					</Typography>
				)}
				{(thoughtLabel || usageLabel) && (
					<Typography
						variant="caption"
						sx={{
							display: "block",
							color: CHAT_TEXT_COLOR,
							opacity: 0.65,
							fontStyle: "italic",
							mb: 0.35,
						}}
					>
						{[thoughtLabel, usageLabel].filter(Boolean).join(" · ")}
						{!isPending && (
							<Tooltip title="Copy this reply's stats">
								<IconButton
									size="small"
									aria-label="Copy message stats"
									onClick={() => this.copyText(this.usageStatsText(msg), "Message stats")}
									sx={{color: CHAT_TEXT_COLOR, opacity: 0.7, p: 0.25, ml: 0.5, verticalAlign: "middle"}}
								>
									<ContentCopyIcon sx={{fontSize: 12}} />
								</IconButton>
							</Tooltip>
						)}
					</Typography>
				)}
				<Box sx={{textAlign: "left"}}>
					{(msg.attachments || []).map((att, i) => this.renderAttachment(att, i, msg.attachments))}
					{this.renderRegionChips(msg)}
				</Box>
				{!isWaiting && !isPending && this.renderMarkdown(msg.content, msg.attachments, msg, geoItems)}
				{!isUser && this.renderHandoffCard(msg)}
				{viewReqs.length > 0 && !isWaiting && !isPending && (
					<Box
						sx={{
							mt: 0.75,
							display: "flex",
							flexWrap: "wrap",
							gap: 0.5,
							alignItems: "center",
						}}
					>
						<Typography variant="caption" sx={{color: CHAT_TEXT_COLOR, opacity: 0.7, mr: 0.25}}>
							View requests
						</Typography>
						{viewReqs.map((vr) => (
							<Box
								key={vr.key}
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 0.25,
									px: 0.6,
									py: 0.15,
									borderRadius: 1,
									border: "1px solid rgba(158,203,255,0.35)",
									bgcolor: "rgba(158,203,255,0.08)",
								}}
							>
								<Tooltip title={vr.reason ? `Go to on slide — ${vr.reason}` : "Go to on slide"}>
									<Box
										component="button"
										type="button"
										aria-label={`Go to view request ${vr.id || ""}`}
										disabled={this.isDashboard()}
										onClick={() => this.goToViewRequest(vr, msg.attachments, msg.id)}
										sx={{
											all: "unset", cursor: this.isDashboard() ? "default" : "pointer",
											color: CHAT_TEXT_COLOR, fontSize: "0.7rem", lineHeight: 1.5,
											"&:hover": {color: "#9ecbff", textDecoration: "underline"},
										}}
									>
										{vr.intent}
										{vr.sourceSite ? ` · ${vr.sourceSite}` : ""}
										{vr.targetMag ? ` · ${vr.targetMag}×` : ""}
									</Box>
								</Tooltip>
								<Tooltip title="Capture this FOV into tray">
									<span>
										<IconButton
											size="small"
											disabled={this.isDashboard() || this.state.capturing || this.state.autoCapturing}
											onClick={() => this.manualCaptureViewRequest(vr, msg.attachments, msg.id)}
											sx={{color: "#9ecbff", p: 0.15}}
										>
											<CameraAltOutlinedIcon sx={{fontSize: 13}} />
										</IconButton>
									</span>
								</Tooltip>
							</Box>
						))}
					</Box>
				)}
				{geoFeatureCount > 0 && (
					<Box
						sx={{
							mt: 1,
							pt: 0.75,
							borderTop: "1px solid rgba(255,255,255,0.1)",
						}}
					>
						{placing && (
							<Typography variant="caption" sx={{display: "block", color: CHAT_TEXT_COLOR, opacity: 0.75, mb: 0.5}}>
								{placing.phase === "navigate" && placing.site
									? `Moving to Image ${placing.site}…`
									: placing.phase === "placed"
										? `Placed on Image ${placing.site || ""}…`
										: "Placing annotations…"}
							</Typography>
						)}
						<Stack
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							sx={{mb: 0.5}}
						>
							<Button
								size="small"
								onClick={() => this.toggleLegendExpanded(msg.id)}
								endIcon={legendOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
								sx={{
									textTransform: "none",
									color: CHAT_TEXT_COLOR,
									opacity: 0.9,
									fontSize: "0.75rem",
									px: 0.5,
								}}
							>
								Features · {primaryFeats.length} primary
								{secondaryFeats.length ? ` · ${secondaryFeats.length} secondary` : ""}
							</Button>
							{!this.isDashboard() && (
								<Button
									size="small"
									variant="text"
									disabled={Boolean(this.state.savingGeoForMsg)}
									onClick={() => this.handleSaveGeoJson(msg)}
									sx={{
										textTransform: "none",
										fontSize: "0.72rem",
										color: "rgba(255,255,255,0.65)",
										minWidth: 0,
									}}
									title="Draw every proposed region on the slide"
								>
									{this.state.savingGeoForMsg === msg.id ? "Placing…" : "Draw all"}
								</Button>
							)}
						</Stack>
						<Collapse in={legendOpen}>
							{primaryFeats.length > 0 && (
								<Box sx={{mb: 0.75}}>
									<Typography
										variant="caption"
										sx={{
											display: "block",
											color: CHAT_TEXT_COLOR,
											opacity: 0.55,
											mb: 0.35,
											letterSpacing: "0.04em",
											textTransform: "uppercase",
											fontSize: "0.65rem",
										}}
									>
										Primary
									</Typography>
									<Box sx={{display: "flex", flexWrap: "wrap", gap: 0.35}}>
										{primaryFeats.map((item) => this.renderFeatureChip(msg, item, {
											drawn: drawnKeys.includes(item.key),
										}))}
									</Box>
								</Box>
							)}
							{secondaryFeats.length > 0 && (
								<Box sx={{mb: 0.5}}>
									<Typography
										variant="caption"
										sx={{
											display: "block",
											color: CHAT_TEXT_COLOR,
											opacity: 0.55,
											mb: 0.35,
											letterSpacing: "0.04em",
											textTransform: "uppercase",
											fontSize: "0.65rem",
										}}
									>
										Secondary
									</Typography>
									<Box sx={{display: "flex", flexWrap: "wrap", gap: 0.35}}>
										{secondaryFeats.map((item) => this.renderFeatureChip(msg, item, {
											drawn: drawnKeys.includes(item.key),
										}))}
									</Box>
								</Box>
							)}
							{this.isDashboard() && (
								<Typography variant="caption" sx={{color: CHAT_TEXT_COLOR, opacity: 0.6}}>
									Open the slide in the viewer to Go to / Draw features.
								</Typography>
							)}
						</Collapse>
					</Box>
				)}
				{showRetry && (
					<Button
						size="small"
						variant="outlined"
						color="secondary"
						onClick={() => this.handleRetry(msg)}
						sx={{mt: 0.75, color: CHAT_TEXT_COLOR, borderColor: "rgba(255,255,255,0.45)"}}
					>
						Retry now
					</Button>
				)}
			</Box>
		);
	};

	renderCaptureTray = () => {
		const captures = this.state.capturedImages || [];
		if (!captures.length) return null;
		return (
			<Box sx={{
				display: "flex",
				flexWrap: "wrap",
				gap: 1,
				p: 0.75,
				borderRadius: 1,
				border: "1px dashed rgba(255,255,255,0.3)",
				flexShrink: 0,
			}}>
				{captures.map((s) => (
					<Box key={s.id} sx={{position: "relative", width: 72}}>
						<Typography variant="caption" sx={{color: CHAT_TEXT_COLOR}}>Image {s.label}</Typography>
						{s.previewUrl ? (
							<img
								src={s.previewUrl}
								alt={s.label}
								style={{width: "100%", height: 54, objectFit: "cover", borderRadius: 3, cursor: "pointer"}}
								onClick={() => s.mapState && this.props.slidemap && animateToState(this.props.slidemap.getView(), s.mapState)}
							/>
						) : (
							<Box sx={{height: 54, bgcolor: "rgba(255,255,255,0.1)", borderRadius: 1}} />
						)}
						<IconButton
							size="small"
							onClick={() => this.removeCapturedImage(s.id)}
							sx={{position: "absolute", top: 12, right: -4, color: CHAT_TEXT_COLOR, bgcolor: "rgba(0,0,0,0.45)", p: 0.15}}
						>
							<CloseIcon sx={{fontSize: 12}} />
						</IconButton>
					</Box>
				))}
			</Box>
		);
	};

	render() {
		if (!hasPermissions([aclPermissions.ai_chat_app])) {
			return <Typography variant="body2" sx={{color: CHAT_TEXT_COLOR}}>No permission for Morpho Chat.</Typography>;
		}
		const isPopout = this.props.variant === "popout";
		const captureCount = (this.state.capturedImages || []).length;
		const captures = this.state.capturedImages || [];
		const captureHint = captures.length
			? `Ask about Image ${captures[0].label}${captures.length > 1 ? `–${captures[captures.length - 1].label}` : ""}…`
			: "Ask…";
		return (
			<Stack
				direction="column"
				spacing={1}
				sx={{
					height: "100%",
					minHeight: isPopout ? 0 : 320,
					flex: isPopout ? 1 : undefined,
					minWidth: 0,
				}}
			>
				{!this.props.hideTitle && <AppTitle title="Morpho Chat" />}
				<Stack direction="row" alignItems="center" spacing={0.5} sx={{px: 0.25, mt: this.props.hideTitle ? 0 : -0.5}}>
					<Tooltip title="Chat history on this slide">
						<IconButton
							size="small"
							onClick={(e) => this.setState({chatMenuAnchor: e.currentTarget})}
							sx={{color: CHAT_TEXT_COLOR, opacity: 0.85}}
						>
							<HistoryIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					{this.state.renameChatId && this.state.renameChatId === this.state.chatId && !this.state.renameInMenu ? (
						<Box sx={{flex: 1, minWidth: 0}}>{this.renderChatRenameField()}</Box>
					) : (
						<Typography
							variant="caption"
							sx={{
								color: CHAT_TEXT_COLOR,
								opacity: 0.8,
								flex: 1,
								minWidth: 0,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
							title={this.state.chatTitle}
							onDoubleClick={() => {
								if (!this.state.viewingArchived) {
									this.startChatRename(this.state.chatId, this.state.chatTitle);
								}
							}}
						>
							{this.state.chatTitle || "New chat"}
						</Typography>
					)}
					<Tooltip title="Rename chat">
						<span>
							<IconButton
								size="small"
								onClick={() => this.startChatRename(this.state.chatId, this.state.chatTitle)}
								disabled={!this.state.chatId || this.state.viewingArchived}
								sx={{color: CHAT_TEXT_COLOR}}
							>
								<EditOutlinedIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="New chat">
						<IconButton size="small" onClick={this.handleNewChat} sx={{color: CHAT_TEXT_COLOR}}>
							<AddCommentOutlinedIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Hide chat from your list (not permanently deleted)">
						<span>
							<IconButton
								size="small"
								onClick={this.handleDeleteChat}
								disabled={!this.state.chatId || this.state.viewingArchived}
								sx={{color: CHAT_TEXT_COLOR}}
							>
								<DeleteOutlineIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Stack>
				{this.state.viewingArchived && (
					<Typography
						variant="caption"
						sx={{
							display: "block",
							px: 0.5,
							py: 0.35,
							mb: 0.25,
							borderRadius: 1,
							bgcolor: "rgba(255,193,7,0.15)",
							color: "#ffe082",
							fontSize: "0.7rem",
						}}
					>
						Archived (admin view) — not permanently deleted. Hidden from normal users.
					</Typography>
				)}
				<Menu
					anchorEl={this.state.chatMenuAnchor}
					open={Boolean(this.state.chatMenuAnchor)}
					onClose={() => {
						if (this.state.renameInMenu) this.commitChatRename();
						this.setState({chatMenuAnchor: null});
					}}
					PaperProps={{sx: {bgcolor: "#1e1e1e", color: "#fff", minWidth: 240, maxWidth: 340}}}
				>
					<MenuItem onClick={this.handleNewChat}>
						<ListItemIcon sx={{color: "#fff", minWidth: 32}}><AddCommentOutlinedIcon fontSize="small" /></ListItemIcon>
						<ListItemText primary="New chat" />
					</MenuItem>
					<Divider sx={{borderColor: "rgba(255,255,255,0.12)"}} />
					{(() => {
						const sessions = this.state.chatSessions || [];
						const open = sessions.filter((s) => !s.archived);
						const archived = sessions.filter((s) => s.archived);
						const rows = [];
						if (!open.length && !archived.length) {
							rows.push(<MenuItem key="empty" disabled>No other chats</MenuItem>);
						}
						open.forEach((s) => {
							const renaming = this.state.renameInMenu && this.state.renameChatId === s.chat_id;
							rows.push(
								<MenuItem
									key={s.chat_id}
									selected={s.chat_id === this.state.chatId}
									onClick={() => {
										if (!renaming) this.handleSwitchChat(s.chat_id);
									}}
									sx={{
										pr: 1,
										"&:hover .chat-menu-edit, & .chat-menu-edit:focus-visible": {opacity: 1},
									}}
								>
									<ListItemText
										primary={renaming ? this.renderChatRenameField({"& .MuiInputBase-input": {color: "#fff", fontSize: "0.875rem", py: 0.25}}) : (s.title || "Chat")}
										secondary={(s.scope === "case" ? `Case${s.home_part ? ` · from ${s.home_part}` : ""} · ` : "")
											+ (s.preview || `${s.message_count || 0} messages`)}
										primaryTypographyProps={{noWrap: true, component: "div"}}
										secondaryTypographyProps={{noWrap: true, sx: {color: "rgba(255,255,255,0.5)"}}}
										sx={{minWidth: 0}}
									/>
									{!renaming && (
										<Tooltip title="Rename chat">
											<IconButton
												className="chat-menu-edit"
												size="small"
												onClick={(e) => {
													e.stopPropagation();
													this.startChatRename(s.chat_id, s.title, true);
												}}
												sx={{color: "#fff", opacity: 0, transition: "opacity 120ms", ml: 0.5, flexShrink: 0}}
											>
												<EditOutlinedIcon sx={{fontSize: 16}} />
											</IconButton>
										</Tooltip>
									)}
								</MenuItem>,
							);
						});
						if (archived.length && this.includeArchivedSessions()) {
							rows.push(
								<MenuItem key="arch-hdr" disabled sx={{opacity: 0.7, fontSize: "0.7rem"}}>
									Archived (not deleted)
								</MenuItem>,
							);
							archived.forEach((s) => {
								rows.push(
									<MenuItem
										key={s.chat_id}
										selected={s.chat_id === this.state.chatId}
										onClick={() => this.handleSwitchChat(s.chat_id)}
									>
										<ListItemText
											primary={`${s.title || "Chat"} · archived`}
											secondary={s.preview || `${s.message_count || 0} messages`}
											primaryTypographyProps={{noWrap: true, sx: {opacity: 0.85}}}
											secondaryTypographyProps={{noWrap: true, sx: {color: "rgba(255,255,255,0.45)"}}}
										/>
									</MenuItem>,
								);
							});
						}
						return rows;
					})()}
					{this.state.chatId && !this.state.viewingArchived && (
						<>
							<Divider sx={{borderColor: "rgba(255,255,255,0.12)"}} />
							<MenuItem onClick={this.handleDeleteChat}>
								<ListItemIcon sx={{color: "#fff", minWidth: 32}}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
								<ListItemText
									primary="Hide from my list"
									secondary="Not permanently deleted — kept for admins"
									secondaryTypographyProps={{sx: {color: "rgba(255,255,255,0.45)", whiteSpace: "normal"}}}
								/>
							</MenuItem>
						</>
					)}
				</Menu>
				{(() => {
					const totals = this.chatUsageTotals();
					if (!totals) return null;
					return (
						<Typography
							variant="caption"
							sx={{color: CHAT_TEXT_COLOR, opacity: 0.65, px: 0.5}}
							title="Token totals for this chat (from Cursor CLI). Billing: Cursor dashboard."
						>
							Chat usage · {formatTokenCount(totals.tokens_in)} in · {formatTokenCount(totals.tokens_out)} out
							{totals.tokens_cache_read > 0 ? ` · ${formatTokenCount(totals.tokens_cache_read)} cache` : ""}
							{totals.tokens_cache_write > 0 ? ` · ${formatTokenCount(totals.tokens_cache_write)} cache write` : ""}
							<Tooltip title="Copy stats for the whole chat">
								<IconButton
									size="small"
									aria-label="Copy chat stats"
									onClick={() => this.copyText(this.chatStatsText(), "Chat stats")}
									sx={{color: CHAT_TEXT_COLOR, opacity: 0.8, p: 0.25, mx: 0.25, verticalAlign: "middle"}}
								>
									<ContentCopyIcon sx={{fontSize: 13}} />
								</IconButton>
							</Tooltip>
							{" · "}
							<a
								href={CURSOR_USAGE_URL}
								target="_blank"
								rel="noopener noreferrer"
								style={{color: "#9ecbff"}}
							>
								Cursor billing ↗
							</a>
						</Typography>
					);
				})()}
				<Box
					ref={this.listRef}
					onScroll={this.onChatListScroll}
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						px: 0.5,
						py: 0.5,
					}}
				>
					{this.state.loadingChat && <CircularProgress size={20} />}
					{(this.state.messages || []).map(this.renderMessage)}
				</Box>
				{this.renderCaptureTray()}
				{!this.isDashboard() && (
					<Stack
						direction="row"
						alignItems="center"
						flexWrap="wrap"
						sx={{flexShrink: 0, px: 0.25, columnGap: 2, rowGap: 0.75}}
					>
						{this.state.chatCase && (
							<FormControlLabel
								sx={{
									ml: 0.25,
									mr: 0,
									alignItems: "center",
									"& .MuiFormControlLabel-label": {color: CHAT_TEXT_COLOR, fontSize: "0.78rem", opacity: 0.9},
								}}
								title={`Share this chat across all ${this.state.chatCase.parts?.length || ""} parts of the case`}
								control={
									<Checkbox
										size="small"
										checked={this.state.chatScope === "case"}
										disabled={this.state.linkingCase || !this.state.chatId || this.state.viewingArchived}
										onChange={(e) => this.toggleCaseLink(e.target.checked)}
										inputProps={{"aria-label": "Link chat to case"}}
										sx={{color: "rgba(255,255,255,0.45)", py: 0.25, "&.Mui-checked": {color: "#9ecbff"}}}
									/>
								}
								label={`Link to case ${this.state.chatCase.case_key || ""}`.trim()
									+ (this.state.chatScope === "case" && this.state.chatCase.current_part
										? ` · on ${this.state.chatCase.current_part} of ${this.state.chatCase.parts?.length || "?"}`
										: "")}
							/>
						)}
						<FormControlLabel
							sx={{
								ml: 0.25,
								mr: 0,
								alignItems: "center",
								"& .MuiFormControlLabel-label": {
									color: CHAT_TEXT_COLOR,
									fontSize: "0.78rem",
									opacity: 0.9,
								},
							}}
							control={
								<Checkbox
									size="small"
									checked={Boolean(this.state.annoRequired)}
									onChange={(e) => this.setState({annoRequired: e.target.checked})}
									sx={{
										color: "rgba(255,255,255,0.45)",
										py: 0.25,
										"&.Mui-checked": {color: "#9ecbff"},
									}}
								/>
							}
							label="Mark regions as annotations"
						/>
						<Tooltip title="On: the AI also reads every full-resolution tile of the slide (slower, whole-slide). Off: it only sees your captured views.">
							<FormControlLabel
								sx={{
									ml: 0.25,
									mr: 0,
									alignItems: "center",
									"& .MuiFormControlLabel-label": {
										color: CHAT_TEXT_COLOR,
										fontSize: "0.78rem",
										opacity: 0.9,
									},
								}}
								control={
									<Checkbox
										size="small"
										checked={this.state.fullAnalysis !== false}
										onChange={(e) => this.setState({fullAnalysis: e.target.checked})}
										sx={{
											color: "rgba(255,255,255,0.45)",
											py: 0.25,
											"&.Mui-checked": {color: "#9ecbff"},
										}}
									/>
								}
								label="Full analysis mode"
							/>
						</Tooltip>
						<Stack direction="row" alignItems="center" spacing={0.35}>
							<Typography
								variant="caption"
								sx={{color: CHAT_TEXT_COLOR, opacity: 0.75, fontSize: "0.72rem"}}
							>
								Auto-capture depth
							</Typography>
							{[0, 1, 2, 3].map((n) => (
								<Button
									key={n}
									size="small"
									variant={this.state.autoViewDepthMax === n ? "contained" : "outlined"}
									onClick={() => this.setAutoViewDepthMax(n)}
									sx={{
										minWidth: 28,
										px: 0.5,
										py: 0,
										fontSize: "0.7rem",
										textTransform: "none",
										color: CHAT_TEXT_COLOR,
										borderColor: "rgba(255,255,255,0.3)",
										...(this.state.autoViewDepthMax === n
											? {bgcolor: "rgba(158,203,255,0.35)"}
											: {}),
									}}
								>
									{n}
								</Button>
							))}
							{this.state.autoViewDepthMax > 0 && this.state.autoViewDepth > 0 && (
								<Typography
									variant="caption"
									sx={{color: CHAT_TEXT_COLOR, opacity: 0.55, fontSize: "0.68rem", ml: 0.25}}
								>
									{this.state.autoViewDepth}/{this.state.autoViewDepthMax}
								</Typography>
							)}
						</Stack>
					</Stack>
				)}
				{this.state.autoCapturing && (
					<Typography
						variant="caption"
						sx={{
							flexShrink: 0,
							px: 0.75,
							color: CHAT_TEXT_COLOR,
							opacity: 0.8,
							fontStyle: "italic",
						}}
					>
						Auto-capturing FOV
						{this.state.autoCaptureProgress
							? ` ${this.state.autoCaptureProgress.done + 1}/${this.state.autoCaptureProgress.total}`
								+ (this.state.autoCaptureProgress.intent
									? ` · ${this.state.autoCaptureProgress.intent}`
									: "")
							: "…"}
					</Typography>
				)}
				{this.isDashboard() && this.props.slide?.id && (
					<Stack direction="row" spacing={0.75} sx={{flexShrink: 0, px: 0.25}}>
						<Button
							size="small"
							variant="outlined"
							color="secondary"
							onClick={() => this.openSlideInViewer({openChat: true})}
							sx={{
								textTransform: "none",
								fontSize: "0.75rem",
								borderColor: "rgba(255,255,255,0.35)",
								color: CHAT_TEXT_COLOR,
							}}
						>
							Open slide in viewer
						</Button>
					</Stack>
				)}
				<Box
					sx={{
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						px: 1,
						py: 0.75,
						borderRadius: 2,
						border: "1px solid rgba(255,255,255,0.18)",
						bgcolor: "rgba(0,0,0,0.25)",
					}}
				>
					<StyledInputBase
						size="small"
						placeholder={
							(this.state.sendQueue || []).length
								? `Queued ${(this.state.sendQueue || []).length} — ask another…`
								: captureHint
						}
						value={this.state.value}
						onChange={(e) => this.setState({value: e.target.value})}
						onKeyPress={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								this.handleSubmit();
							}
						}}
						sx={{flex: 1, minWidth: 0, "& input": {py: 0.75}}}
					/>
					<Button
						size="small"
						onClick={this.openModelMenu}
						endIcon={<KeyboardArrowDownIcon sx={{fontSize: "14px !important", ml: -0.25}} />}
						sx={{
							flexShrink: 0,
							textTransform: "none",
							color: "rgba(255,255,255,0.88)",
							fontSize: "0.78rem",
							fontWeight: 500,
							minWidth: 0,
							px: 0.75,
							py: 0.25,
							borderRadius: 1,
							letterSpacing: 0.1,
							"&:hover": {bgcolor: "rgba(255,255,255,0.08)"},
							...(this.state.modelMenuAnchor ? {bgcolor: "rgba(255,255,255,0.1)"} : {}),
						}}
					>
						{this.cursorSelectionLabel()}
					</Button>
					{!this.isDashboard() && (
						<Tooltip title={`Attach viewport image (optional) · ${captureCount}/${MAX_AI_SITES}`}>
							<span>
								<IconButton
									size="small"
									onClick={this.handleCaptureImage}
									disabled={this.state.capturing || captureCount >= MAX_AI_SITES}
									sx={{color: CHAT_TEXT_COLOR}}
									aria-label="Capture image"
								>
									{this.state.capturing
										? <CircularProgress size={16} color="inherit" />
										: <CameraAltOutlinedIcon fontSize="small" />}
								</IconButton>
							</span>
						</Tooltip>
					)}
					<IconButton
						size="small"
						color="secondary"
						onClick={this.handleSubmit}
						disabled={!this.state.value.trim()}
						sx={{
							color: CHAT_TEXT_COLOR,
							bgcolor: "rgba(255,255,255,0.12)",
							"&:hover": {bgcolor: "rgba(255,255,255,0.2)"},
							"&.Mui-disabled": {color: "rgba(255,255,255,0.3)"},
						}}
						aria-label="Send"
					>
						<SmartToyOutlinedIcon fontSize="small" />
					</IconButton>
				</Box>
				{this.renderModelMenus()}
				<Dialog
					open={Boolean(this.state.lightbox)}
					onClose={() => this.setState({lightbox: null})}
					maxWidth="md"
				>
					<DialogContent sx={{p: 1, bgcolor: "#111"}}>
						{this.state.lightbox && (
							<>
								<Typography variant="caption" sx={{color: "#fff", mb: 0.5, display: "block"}}>
									Image {this.state.lightbox.label}
								</Typography>
								<img
									src={this.state.lightbox.src}
									alt={this.state.lightbox.label}
									style={{maxWidth: "100%", maxHeight: "80vh", display: "block"}}
								/>
							</>
						)}
					</DialogContent>
				</Dialog>
			</Stack>
		);
	}
}

const mapStateToProps = (state, ownProps) => {
	const {activeMapId, activeAnnoDrawer, selectedDrawColor} = state.gammaStateReducer;
	const map = state.mapsStateReducer[activeMapId] ?? {};
	return {
		activeMapId,
		activeAnnoDrawer,
		selectedDrawColor,
		slide: ownProps.slide || map.slide,
		slidemap: ownProps.slidemap !== undefined ? ownProps.slidemap : map.slidemap,
		x: map.x,
		y: map.y,
		z: map.z,
		r: map.r,
		displaySetting: map.displaySetting,
		extension: state.screenshotStateReducer?.extension,
		caseFiles: state.caseReducer?.case_files || [],
	};
};

export default connect(mapStateToProps)(AiChatApp);
